<template>
  <div class="yuklama-page">
    <div class="page-header">
      <div class="breadcrumb">
        <span class="crumb-link" @click="$router.push('/')">Asosiy</span>
        <span class="separator">/</span>
        <span class="crumb-link" @click="$router.back()">Kafedra yuklamalari</span>
        <span class="separator">/</span>
        <span class="crumb-current">{{ kafedraName || "Kafedra" }}</span>
        <span class="separator" v-if="filters.oquvYili">/</span>
        <span class="crumb-current" v-if="filters.oquvYili">{{ filters.oquvYili }}</span>
        <span class="separator" v-if="filters.semestrTuri">/</span>
        <span class="crumb-current" v-if="filters.semestrTuri">{{ filters.semestrTuri === 'kuzgi' ? 'Kuzgi' : filters.semestrTuri === 'bahorgi' ? 'Bahorgi' : 'Hammasi' }} semestr</span>
        <span class="separator" v-if="filters.kurs">/</span>
        <span class="crumb-current" v-if="filters.kurs">{{ filters.kurs }}-kurs</span>
      </div>
    </div>

    <div class="actions-row">
      <div class="filters-group">
        <input type="text" class="filter-input" v-model="filters.oquvYili" placeholder="O'quv yili (e.g., 2026-2027)" @keyup.enter="applyYearFilter" />
        <button class="filter-btn" :class="{ active: filters.semestrTuri === '' }" @click="setSemester('')">Hammasi</button>
        <button class="filter-btn" :class="{ active: filters.semestrTuri === 'kuzgi' }" @click="setSemester('kuzgi')">Kuzgi</button>
        <button class="filter-btn" :class="{ active: filters.semestrTuri === 'bahorgi' }" @click="setSemester('bahorgi')">Bahorgi</button>
        <div class="filter-divider"></div>
        <span class="filter-label">Kurs:</span>
        <button class="filter-btn" :class="{ active: filters.kurs === '' }" @click="setKurs('')">Hammasi</button>
        <button v-for="k in availableCourses" :key="k" class="filter-btn" :class="{ active: String(filters.kurs) === String(k) }" @click="setKurs(String(k))">{{ k }}-kurs</button>
      </div>

      <div class="action-buttons-right">
        <!-- 🔄 Qayta sync tugmasi (chiqarish yoki o'chirib qo'yish mumkin) -->
        <button 
          v-if="buttonsConfig.showSync" 
          class="btn-sync" 
          @click="reSync" 
          :disabled="syncing"
          title="HEMIS bilan qayta sinxronizatsiya qilish"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-right: 6px;" :class="{ spinning: syncing }">
            <polyline points="23 4 23 10 17 10"></polyline>
            <polyline points="1 20 1 14 7 14"></polyline>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
          </svg>
          {{ syncing ? "Sync bo'lmoqda..." : "Qayta sync" }}
        </button>

        <!-- 🛡️ Baza nusxasi (Backup) tugmasi Excel yonida (chiqarish yoki o'chirib qo'yish mumkin) -->
        <div v-if="buttonsConfig.showBackup && isSuperAdmin" class="backup-action-container">
          <button 
            class="btn-backup-quick" 
            @click="triggerQuickBackup" 
            :disabled="backupLoading"
            title="Tezkor zaxira nusxasi olish (Snapshot)"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-right: 6px;">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            {{ backupLoading ? "Nusxa olinmoqda..." : "Baza nusxasi (Backup)" }}
          </button>
          <button 
            class="btn-backup-caret"
            @click.stop="toggleBackupDropdown"
            title="Qo'shimcha backup amallari"
          >
            ▾
          </button>

          <!-- Dropdown amallar -->
          <div v-if="showBackupDropdown" class="backup-menu-dropdown" @click.stop>
            <div class="menu-item" @click="triggerQuickBackup">
              <span class="menu-icon">⚡</span>
              <div class="menu-content">
                <div class="menu-title">Tezkor nuqta olish (Snapshot)</div>
                <div class="menu-desc">Hozirgi holatni 1 soniyada zaxiralash</div>
              </div>
            </div>
            <div class="menu-divider"></div>
            <div class="menu-item" @click="goToBackupPage">
              <span class="menu-icon">🔄</span>
              <div class="menu-content">
                <div class="menu-title">Barcha nusxalar & Rollback</div>
                <div class="menu-desc">Kechagi yoki 3 min oldingi holatga qaytarish</div>
              </div>
            </div>
          </div>
        </div>

        <!-- 📗 Excel (.xlsx) eksport -->
        <button class="btn-export" @click="exportToExcel" title="Excel (.xlsx) formatida yuklab olish">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-right: 6px;">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          Export yuklama (.xlsx)
        </button>

        <!-- ⚙️ Tugmalarni chiqarish / o'chirib qo'yish (Sozlama) - Super Admin uchun -->
        <div v-if="isSuperAdmin" class="settings-action-container">
          <button 
            class="btn-settings-toggle" 
            @click.stop="toggleConfigModal"
            title="Qayta sync va Baza nusxasi tugmalarini chiqarish yoki o'chirib qo'yish"
          >
            ⚙️ Tugmalar
          </button>

          <div v-if="showConfigModal" class="settings-popover" @click.stop>
            <div class="popover-title-row">
              <span class="popover-title">Tugmalar boshqaruvi</span>
              <button class="btn-close-pop" @click="showConfigModal = false">✕</button>
            </div>
            <p class="popover-subtitle">Yuklama sahifasidagi tugmalarni chiqarish yoki o'chirib qo'yish:</p>
            <div class="popover-options">
              <label class="pop-check-label">
                <input type="checkbox" v-model="buttonsConfig.showSync" @change="saveButtonsConfig" />
                <span>🔄 "Qayta sync" tugmasi</span>
              </label>
              <label class="pop-check-label">
                <input type="checkbox" v-model="buttonsConfig.showBackup" @change="saveButtonsConfig" />
                <span>🛡️ "Baza nusxasi" tugmasi</span>
              </label>
            </div>
            <div class="popover-footer">
              <span class="text-xs text-gray-400">O'zgarish darhol saqlanadi</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="table-wrapper">
      <div v-if="loading" class="loading-overlay">
        <div class="spinner"></div>
        <span>Ma'lumotlar yuklanmoqda...</span>
      </div>

      <div class="table-scroll">
        <table class="yuklama-table" ref="yuklamaTable">
          <thead>
            <tr>
              <th rowspan="3" class="th-base th-num">#</th>
              <th rowspan="3" class="th-base th-fan">Fanlar</th>
              <th rowspan="3" class="th-base th-fak">Fakultet</th>
              <th rowspan="3" class="th-base th-yon">Ta'lim yo'nalishi</th>
              <th rowspan="3" class="th-base th-guruh-nomi">Guruh nomi</th>
              <th rowspan="3" class="th-base th-kurs-col" :class="{ 'th-kurs-active': !!filters.kurs }">
                <div class="th-kurs-wrapper">
                  <div class="kurs-filter-btn-wrap">
                    <button
                      type="button"
                      class="th-kurs-filter-btn"
                      :class="{ active: !!filters.kurs }"
                      @click.stop="toggleKursDropdown"
                      :title="filters.kurs ? `${filters.kurs}-kurs tanlangan (filtri o'chirish uchun bosing)` : 'Kurs bo\'yicha filtrlash'"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                      </svg>
                      <span v-if="filters.kurs" class="th-kurs-badge">{{ filters.kurs }}</span>
                    </button>

                    <div v-if="showKursDropdown" class="kurs-header-dropdown" @click.stop>
                      <div class="kurs-dropdown-header">
                        <span>Kurs filtri</span>
                        <button class="kurs-close-btn" @click="showKursDropdown = false">×</button>
                      </div>
                      <div
                        class="kurs-dropdown-option"
                        :class="{ selected: filters.kurs === '' }"
                        @click="setKurs('')"
                      >
                        <span class="opt-dot"></span>
                        Hammasi
                      </div>
                      <div
                        v-for="k in availableCourses"
                        :key="k"
                        class="kurs-dropdown-option"
                        :class="{ selected: String(filters.kurs) === String(k) }"
                        @click="setKurs(String(k))"
                      >
                        <span class="opt-dot"></span>
                        {{ k }}-kurs
                      </div>
                    </div>
                  </div>
                  <div class="vertical-text">Kurs</div>
                </div>
              </th>
              <th rowspan="3" class="th-base"><div class="vertical-text">Talaba soni</div></th>
              <th rowspan="3" class="th-base"><div class="vertical-text">Oqim soni</div></th>
              <th rowspan="3" class="th-base"><div class="vertical-text">Guruh soni</div></th>
              <th rowspan="3" class="th-base"><div class="vertical-text">Kichik guruh soni</div></th>

              <th colspan="5" class="th-group th-audit">Auditoriya mashg'uloti</th>
              <th colspan="22" class="th-group th-tashqari">Auditoriyadan tashqari mashg'ulotlar</th>
              <th rowspan="3" class="th-base"><div class="vertical-text">Jami</div></th>
            </tr>
            <tr>
              <th rowspan="2" class="th-base th-audit-cell"><div class="vertical-text">Ma'ruza</div></th>
              <th rowspan="2" class="th-base th-audit-cell"><div class="vertical-text">Amaliy mashg'ulot</div></th>
              <th rowspan="2" class="th-base th-audit-cell"><div class="vertical-text">Semenar mashg'ulot</div></th>
              <th rowspan="2" class="th-base th-audit-cell"><div class="vertical-text">Laboratoriya mashg'ulot</div></th>
              <th rowspan="2" class="th-base th-audit-cell th-jami"><div class="vertical-text">Jami auditoriya soati</div></th>
              
              <th rowspan="2" class="th-base th-tashqari-cell"><div class="vertical-text">Kurs ishi(Talaba soni*2)</div></th>
              <th rowspan="2" class="th-base th-tashqari-cell"><div class="vertical-text">Kurs ishi himoyasi (Talaba soni*0,4)</div></th>
              <th rowspan="2" class="th-base th-tashqari-cell"><div class="vertical-text">Kurs loyihasi (Talaba soni*3)</div></th>
              <th rowspan="2" class="th-base th-tashqari-cell"><div class="vertical-text">Kurs loyihasi himoyasi (Talaba soni*0,6)</div></th>
              <th rowspan="2" class="th-base th-tashqari-cell"><div class="vertical-text">Hisob chizma ishi (Talaba soni*0,3)</div></th>
              <th rowspan="2" class="th-base th-tashqari-cell"><div class="vertical-text">Oraliq nazorat (Talaba soni*0,2)</div></th>
              <th rowspan="2" class="th-base th-tashqari-cell"><div class="vertical-text">Yakuniy nazorat (Talaba soni*0,3)</div></th>
              <th rowspan="2" class="th-base th-tashqari-cell"><div class="vertical-text">Malakaviy amaliyot individual (talaba soni*2)</div></th>
              <th rowspan="2" class="th-base th-tashqari-cell"><div class="vertical-text">Malakaviy amaliyot universitet hududida (3*6*hafta soni*guruh soni)</div></th>
              <th rowspan="2" class="th-base th-tashqari-cell"><div class="vertical-text">Malakaviy amaliyot universitetdan tashqari hududda (6*6*hafta soni*guruh soni)</div></th>
              <th rowspan="2" class="th-base th-tashqari-cell"><div class="vertical-text">BMI rahbarlik (Boshqa ta'lim yo'nalishlari talaba soni*25, texnik yo'nalishlarga talaba soni*30)</div></th>
              <th rowspan="2" class="th-base th-tashqari-cell"><div class="vertical-text">BMI/DAK (Talaba soni*0,4)</div></th>
              <th rowspan="2" class="th-base th-tashqari-cell"><div class="vertical-text">Magistrlik IPF va amaliyotga rahbarlik (Talaba soni*50)</div></th>
              <th rowspan="2" class="th-base th-tashqari-cell"><div class="vertical-text">MD - ITI ga rahbarlik qilish (Talaba soni*50)</div></th>
              <th rowspan="2" class="th-base th-tashqari-cell"><div class="vertical-text">MD/DAK (Talaba soni*0,8)</div></th>
              <th rowspan="2" class="th-base th-tashqari-cell"><div class="vertical-text">Doktorantga (stajyor tadqiqotchiga talaba soni*75) rahbarlik Talaba soni*100)</div></th>
              <th rowspan="2" class="th-base th-tashqari-cell"><div class="vertical-text">YaDA yozma ish guruh soni*3</div></th>
              <th rowspan="2" class="th-base th-tashqari-cell"><div class="vertical-text">YaDA yozma ish tekshirish uchun talaba soni*0,2</div></th>
              <th rowspan="2" class="th-base th-tashqari-cell"><div class="vertical-text">YaDA test guruh soni*1</div></th>
              <th rowspan="2" class="th-base th-tashqari-cell"><div class="vertical-text">Ochiq leksiya (dars soni soni*5)</div></th>
              <th rowspan="2" class="th-base th-tashqari-cell"><div class="vertical-text">Respublika ta'lim muassasalarida markazlarida malaka oshirishlarni o'tash</div></th>
              <th rowspan="2" class="th-base th-tashqari-cell th-jami"><div class="vertical-text">Jami auditoriyadan tashqari soat</div></th>
            </tr>
            <tr></tr>
          </thead>
          <tbody>
            <tr v-if="!loading && displayRows.length === 0">
              <td colspan="39" class="td-empty">Ma'lumot topilmadi</td>
            </tr>
            <tr
              v-for="(row, i) in displayRows"
              :key="row._id || row.itemId || ('row_' + i)"
              class="data-row"
              :class="{ 'row-secondary': row.isSyntheticGroup, 'row-primary': row.isPrimaryStream }">
              <td class="td-center td-num">
                {{ (currentPage - 1) * limit + i + 1 }}
              </td>
              <td class="td-left td-fan" :class="{'editable-cell': row.isCustom}" @click="row.isCustom && openEditModal(row, 'customSubject', 'Fan nomini kiriting', 'text')">
                <button
                  v-if="isEditable && !row.isSyntheticGroup && row._id && !String(row._id).startsWith('synth_')"
                  class="btn-delete-row"
                  @click.stop="confirmDeleteRow(row)"
                  title="Qatorni o'chirish"
                >
                  ✕
                </button>

                {{ getSubjectNameDisplay(row) }}
                <span class="fan-code" v-if="getSubjectCodeDisplay(row)">| {{ getSubjectCodeDisplay(row) }}</span>
                <span class="sem-badge" v-if="row.semester?.name || row.semestrTuri" style="font-size: 11px; color: #1e40af; background: #e0e7ff; padding: 2px 6px; border-radius: 4px; margin-left: 6px; font-weight: 600; display: inline-block;">
                  {{ row.semester?.name || (row.semestrTuri === 'kuzgi' ? 'Kuzgi' : 'Bahorgi') }}
                </span>
              </td>
              <td class="td-left" :class="{'editable-cell': row.isCustom}" @click="row.isCustom && openEditModal(row, 'customFaculty', 'Fakultet nomini kiriting', 'text')">{{ row.faculty?.name || row.customFaculty || row.specialty?.name || "—" }}</td>
              <td class="td-left td-yon" :class="{'editable-cell': row.isCustom}" @click="row.isCustom && openEditModal(row, 'customSpecialty', 'Ta\'lim yo\'nalishini kiriting', 'text')">{{ getRowSpecialty(row) || "—" }}</td>
              <td class="td-center td-guruh-nomi editable-cell" @click="openSubjectModal(row)">{{ getFullGuruhNomi(row) }}</td>
              <td class="td-center" :class="{'editable-cell': row.isCustom}" @click="row.isCustom && openEditModal(row, 'customKurs', 'Kursni kiriting')">{{ row.customKurs || getKurs(row) }}</td>
              <td class="td-center editable-cell" @click="openSubjectModal(row, 'maruza')">
                {{ (row.guruhNomi || row.isCustom) ? (row.talabalarSoni || 0) : 0 }}
              </td>
              <td class="td-center editable-cell" @click="openEditModal(row, 'oqimSoni', 'Oqim sonini kiriting', 'text')">{{ row.oqimSoni || row.oqim || 0 }}</td>
              <td class="td-center editable-cell" @click="openEditModal(row, 'guruhSoni', 'Guruh sonini kiriting', 'text')">{{ row.guruhSoni || (row.guruhNomi ? row.guruhNomi.split(',').filter(Boolean).length : 1) }}</td>
              <td class="td-center editable-cell" @click="openEditModal(row, 'kichikGuruhSoni', 'Kichik guruh sonini kiriting', 'text')">{{ row.kichikGuruhSoni || 0 }}</td>

              <td class="td-center td-audit td-maruza editable-cell" :class="{ 'td-amaliy-multiplied': row.guruhNomi && row.guruhNomi.split(',').length > 1 }" @click="openSubjectModal(row, 'maruza')" title="Ma'ruza / Guruh tahrirlash">{{ calc(row).audit.maruza }}</td>
              <td class="td-center td-audit td-amaliy editable-cell" :class="{ 'td-amaliy-multiplied': row.guruhNomi && row.guruhNomi.split(',').length > 1 }" @click="openSubjectModal(row, 'amaliy')" title="Amaliy / Guruh tahrirlash">{{ calc(row).audit.amaliy }}</td>
              <td class="td-center td-audit editable-cell" @click="openSubjectModal(row, 'seminar')" title="Semenar / Guruh tahrirlash">{{ calc(row).audit.seminar }}</td>
              <td class="td-center td-audit editable-cell" :class="{ 'td-lab-multiplied': row.kichikGuruhSoni > 1 && calc(row).audit.laboratoriya > 0 }" @click="openSubjectModal(row, 'laboratoriya')" title="Laboratoriya / Guruh tahrirlash">{{ calc(row).audit.laboratoriya }}</td>
              <td class="td-center td-audit td-jami-val">
                {{ calc(row).jamiAudit }}
              </td>
              <td class="td-center td-tashqari editable-cell" @click="openTashqariModal(row, 'kursIshi', 'Kurs ishi (Talaba soni*2)')">{{ calc(row).kursIshi }}</td>
              <td class="td-center td-tashqari editable-cell" @click="openTashqariModal(row, 'kursIshiHimoya', 'Kurs ishi himoyasi (Talaba soni*0,4)')">{{ calc(row).kursIshiHimoya }}</td>
              <td class="td-center td-tashqari editable-cell" @click="openTashqariModal(row, 'kursLoyihasi', 'Kurs loyihasi (Talaba soni*3)')">{{ calc(row).kursLoyihasi }}</td>
              <td class="td-center td-tashqari editable-cell" @click="openTashqariModal(row, 'kursLoyihasiHimoya', 'Kurs loyihasi himoyasi (Talaba soni*0,6)')">{{ calc(row).kursLoyihasiHimoya }}</td>
              <td class="td-center td-tashqari editable-cell" @click="openTashqariModal(row, 'hisobChizma', 'Hisob chizma ishi (Talaba soni*0,3)')">{{ calc(row).hisobChizma }}</td>
              <td class="td-center td-tashqari editable-cell" @click="openTashqariModal(row, 'oraliq', 'Oraliq nazorat (Talaba soni*0,2)')">{{ calc(row).oraliq }}</td>
              <td class="td-center td-tashqari editable-cell" @click="openTashqariModal(row, 'yakuniy', 'Yakuniy nazorat (Talaba soni*0,3)')">{{ calc(row).yakuniy }}</td>
              <td class="td-center td-tashqari editable-cell" @click="openTashqariModal(row, 'amaliyotInd', 'Malakaviy amaliyot individual (talaba soni*2)')">{{ calc(row).amaliyotInd }}</td>
              <td class="td-center td-tashqari editable-cell" @click="openTashqariModal(row, 'amaliyotUni', 'Malakaviy amaliyot universitet hududida (3*6*hafta soni*guruh soni)')">{{ calc(row).amaliyotUni }}</td>
              <td class="td-center td-tashqari editable-cell" @click="openTashqariModal(row, 'amaliyotTash', 'Malakaviy amaliyot universitetdan tashqari hududda (6*6*hafta soni*guruh soni)')">{{ calc(row).amaliyotTash }}</td>
              <td class="td-center td-tashqari editable-cell" @click="openTashqariModal(row, 'bmiRahbar', 'BMI rahbarlik (talaba soni*25 yoki *30)')">{{ calc(row).bmiRahbar }}</td>
              <td class="td-center td-tashqari editable-cell" @click="openTashqariModal(row, 'bmiDak', 'BMI/DAK (Talaba soni*0,4)')">{{ calc(row).bmiDak }}</td>
              <td class="td-center td-tashqari editable-cell" @click="openTashqariModal(row, 'magistr', 'Magistrlik IPF va amaliyotga rahbarlik (Talaba soni*50)')">{{ calc(row).magistr }}</td>
              <td class="td-center td-tashqari editable-cell" @click="openTashqariModal(row, 'mdIti', 'MD - ITI ga rahbarlik qilish (Talaba soni*50)')">{{ calc(row).mdIti }}</td>
              <td class="td-center td-tashqari editable-cell" @click="openTashqariModal(row, 'mdDak', 'MD/DAK (Talaba soni*0,8)')">{{ calc(row).mdDak }}</td>
              <td class="td-center td-tashqari editable-cell" @click="openTashqariModal(row, 'doktorant', 'Doktorantga rahbarlik (Talaba soni*100)')">{{ calc(row).doktorant }}</td>
              <td class="td-center td-tashqari editable-cell" @click="openTashqariModal(row, 'yadaYozmaGuruh', 'YaDA yozma ish (guruh soni*3)')">{{ calc(row).yadaYozmaGuruh }}</td>
              <td class="td-center td-tashqari editable-cell" @click="openTashqariModal(row, 'yadaYozmaTalaba', 'YaDA yozma ish tekshirish (talaba soni*0,2)')">{{ calc(row).yadaYozmaTalaba }}</td>
              <td class="td-center td-tashqari editable-cell" @click="openTashqariModal(row, 'yadaTest', 'YaDA test (guruh soni*1)')">{{ calc(row).yadaTest }}</td>
              <td class="td-center td-tashqari editable-cell" @click="openTashqariModal(row, 'ochiqLeksiya', 'Ochiq leksiya (dars soni*5)')">{{ calc(row).ochiqLeksiya }}</td>
              <td class="td-center td-tashqari editable-cell" @click="openTashqariModal(row, 'malakaOshirish', 'Respublika ta\'lim muassasalarida markazlarida malaka oshirishlarni o\'tash')">{{ calc(row).malakaOshirish }}</td>
              <td class="td-center td-tashqari td-jami-val">{{ calc(row).jamiTashqari }}</td>
              <td class="td-center td-jami-val">{{ calc(row).jami }}</td>
            </tr>
          </tbody>
          <tfoot v-if="rows.length > 0">
            <tr class="totals-row">
              <td class="td-center td-total-label" colspan="10">Jami</td>
              <td class="td-center td-total td-audit">{{ totals.maruza }}</td>
              <td class="td-center td-total td-audit">{{ totals.amaliy }}</td>
              <td class="td-center td-total td-audit">{{ totals.seminar }}</td>
              <td class="td-center td-total td-audit">{{ totals.laboratoriya }}</td>
              <td class="td-center td-total td-audit td-jami-val">{{ totals.jamiAudit }}</td>
              <td class="td-center td-total td-tashqari">{{ totals.kursIshi }}</td>
              <td class="td-center td-total td-tashqari">{{ totals.kursIshiHimoya }}</td>
              <td class="td-center td-total td-tashqari">{{ totals.kursLoyihasi }}</td>
              <td class="td-center td-total td-tashqari">{{ totals.kursLoyihasiHimoya }}</td>
              <td class="td-center td-total td-tashqari">{{ totals.hisobChizma }}</td>
              <td class="td-center td-total td-tashqari">{{ totals.oraliq }}</td>
              <td class="td-center td-total td-tashqari">{{ totals.yakuniy }}</td>
              <td class="td-center td-total td-tashqari">{{ totals.amaliyotInd }}</td>
              <td class="td-center td-total td-tashqari">{{ totals.amaliyotUni }}</td>
              <td class="td-center td-total td-tashqari">{{ totals.amaliyotTash }}</td>
              <td class="td-center td-total td-tashqari">{{ totals.bmiRahbar }}</td>
              <td class="td-center td-total td-tashqari">{{ totals.bmiDak }}</td>
              <td class="td-center td-total td-tashqari">{{ totals.magistr }}</td>
              <td class="td-center td-total td-tashqari">{{ totals.mdIti }}</td>
              <td class="td-center td-total td-tashqari">{{ totals.mdDak }}</td>
              <td class="td-center td-total td-tashqari">{{ totals.doktorant }}</td>
              <td class="td-center td-total td-tashqari">{{ totals.yadaYozmaGuruh }}</td>
              <td class="td-center td-total td-tashqari">{{ totals.yadaYozmaTalaba }}</td>
              <td class="td-center td-total td-tashqari">{{ totals.yadaTest }}</td>
              <td class="td-center td-total td-tashqari">{{ totals.ochiqLeksiya }}</td>
              <td class="td-center td-total td-tashqari">{{ totals.malakaOshirish }}</td>
              <td class="td-center td-total td-tashqari td-jami-val">{{ totals.jamiTashqari }}</td>
              <td class="td-center td-total td-jami-val">{{ totals.jami }}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div class="pagination" v-if="displayRows.length > 0">
        <span class="total-info">Jami: {{ displayRows.length }} ta</span>
        <div class="page-btns">
          <button
            @click="changePage(currentPage - 1)"
            :disabled="currentPage === 1"
            class="page-btn">
            ‹
          </button>
          <span class="page-info">{{ currentPage }} / {{ pageCount }}</span>
          <button
            @click="changePage(currentPage + 1)"
            :disabled="currentPage >= pageCount"
            class="page-btn">
            ›
          </button>
        </div>
      </div>

      <!-- Add Custom Workload Section -->
      <div class="custom-workload-section" v-if="isEditable">
        <h4 class="cw-title">Qo'shimcha yuklama qo'shish</h4>
        <div class="cw-controls">
          <select v-model="selectedCustomWorkload" class="filter-select cw-select">
            <option value="">Yuklama turini tanlang...</option>
            <option value="amaliyotInd">Malakaviy amaliyot individual</option>
            <option value="amaliyotUni">Malakaviy amaliyot universitet hududida</option>
            <option value="amaliyotTash">Malakaviy amaliyot universitetdan tashqari hududda</option>
            <option value="bmiRahbar">BMI rahbarlik</option>
            <option value="bmiDak">BMI/DAK</option>
            <option value="magistr">Magistrlik IPF va amaliyotga rahbarlik</option>
            <option value="mdIti">MD - ITI ga rahbarlik qilish</option>
            <option value="mdDak">MD/DAK</option>
            <option value="doktorant">Doktorantga (stajyor tadqiqotchiga) rahbarlik</option>
            <option value="yadaYozmaGuruh">YaDA yozma ish guruh soni</option>
            <option value="yadaYozmaTalaba">YaDA yozma ish tekshirish</option>
            <option value="yadaTest">YaDA test</option>
            <option value="ochiqLeksiya">Ochiq leksiya</option>
            <option value="malakaOshirish">Respublika ta'lim muassasalarida markazlarida malaka oshirishlarni o'tash</option>
          </select>
          <button class="btn-export btn-add-custom" @click="addCustomWorkload" :disabled="!selectedCustomWorkload || addingCustom">
            <span v-if="addingCustom">Qo'shilmoqda...</span>
            <span v-else>+ Jadvalga qo'shish</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Edit Modal -->
    <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content" :class="{ 'modal-tashqari': isTashqariEdit }">
        <div class="modal-header">
          <h3>{{ modalTitle }}</h3>
          <button class="btn-close" @click="closeModal">×</button>
        </div>
        
        <!-- Standard Edit Modal Body -->
        <div v-if="!isTashqariEdit" class="modal-body">
          <label>{{ modalTitle }} (ixtiyoriy):</label>
          <input :type="inputType" v-model="editValue" class="modal-input" />
        </div>

        <!-- Tashqari Workload Multiplier Edit Modal Body -->
        <div v-else class="modal-body tashqari-body">
          <div class="formula-info">
            <span class="formula-label">Formula:</span>
            <code class="formula-code">{{ getActiveFormulaText() }}</code>
          </div>

          <!-- Selection type fields: BMI or Doktorant -->
          <div v-if="tashqariConfig.type === 'selection'" class="selection-section">
            <div class="form-group selection-group">
              <label>Turi:</label>
              <div class="radio-options">
                <label v-for="opt in tashqariConfig.options" :key="opt.value" class="radio-label">
                  <input type="radio" :value="opt.value" v-model="tashqariSelectVal" @change="recalcTashqari" />
                  {{ opt.text }}
                </label>
              </div>
            </div>
            <div class="form-group">
              <label>{{ tashqariConfig.label }}:</label>
              <input type="number" v-model.number="tashqariInputVal" class="modal-input" @input="recalcTashqari" placeholder="Qiymatni kiriting" />
            </div>
          </div>

          <!-- Multi variable fields: Amaliyot (Universitet/Tashqari) -->
          <div v-else-if="tashqariConfig.type === 'multi_vars'" class="multi-inputs">
            <div class="form-group">
              <label>Hafta soni:</label>
              <input type="number" v-model.number="tashqariHafta" class="modal-input" @input="recalcTashqari" />
            </div>
            <div class="form-group">
              <label>Guruh soni:</label>
              <input type="number" v-model.number="tashqariGuruh" class="modal-input" @input="recalcTashqari" />
            </div>
          </div>
          
          <!-- Standard multiplier fields -->
          <div v-else class="form-group">
            <label>{{ tashqariConfig.label }}:</label>
            <input type="number" v-model.number="tashqariInputVal" class="modal-input" @input="recalcTashqari" placeholder="Qiymatni kiriting" />
          </div>

          <!-- Real-time calculation preview -->
          <div class="calc-preview">
            <span class="preview-title">Hisoblangan natija:</span>
            <div class="preview-expr">
              {{ getTashqariExpression() }} = <strong class="preview-total-val">{{ editValue }}</strong> soat
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn-clear" @click="clearValue">O'chirish</button>
          <button class="btn-confirm" @click="confirmValue">Tasdiqlash</button>
        </div>
      </div>
    </div>

    <!-- Subject Edit Modal (Group Name & Students & Audit Hours) -->
    <div v-if="showSubjectModal" class="modal-overlay" @click.self="closeSubjectModal">
      <div class="modal-content subject-modal-content">
        <div class="modal-header">
          <h3>Fanni tahrirlash va Guruh biriktirish</h3>
          <button class="btn-close" @click="closeSubjectModal">×</button>
        </div>

        <!-- Training Type Tabs -->
        <div class="tt-tab-row">
          <button 
            type="button"
            class="tt-tab" 
            :class="{ active: activeTrainingType === 'maruza' }"
            @click="setTrainingType('maruza')">
            📘 Ma'ruza
          </button>
          <button 
            type="button"
            class="tt-tab" 
            :class="{ active: activeTrainingType === 'amaliy' }"
            @click="setTrainingType('amaliy')">
            📗 Amaliy
          </button>
          <button 
            type="button"
            class="tt-tab" 
            :class="{ active: activeTrainingType === 'seminar' }"
            @click="setTrainingType('seminar')">
            📙 Semenar
          </button>
          <button 
            type="button"
            class="tt-tab" 
            :class="{ active: activeTrainingType === 'laboratoriya' }"
            @click="setTrainingType('laboratoriya')">
            🔬 Laboratoriya
          </button>
        </div>

        <div class="modal-body subject-modal-body">
          <div class="form-group">
            <label>{{ subjectModalRow?.subject?.name || 'Guruh nomi' }}:</label>
            <input type="text" v-model="subjectModalGuruh" class="modal-input" placeholder="Guruh nomini kiriting" />
            <div v-if="parsedGuruhs.length > 0" style="display: flex; gap: 6px; flex-wrap: wrap; margin-top: 6px;">
              <span v-for="(gName, i) in parsedGuruhs" :key="i" style="background: #f1f5f9; padding: 4px 8px; border-radius: 4px; font-size: 11px; display: inline-flex; align-items: center; gap: 6px; border: 1px solid #cbd5e1; color: #334155;">
                {{ getFullGuruhNomiFor(gName, groupSpecialtyMap[gName] || guruhSpecialtyMap[gName] || subjectModalRow?.specialty?.name || subjectModalRow?.customSpecialty) }}
                <span @click="removeGuruh(gName)" style="cursor: pointer; color: #ef4444; font-weight: bold; background: #fff; border-radius: 50%; width: 16px; height: 16px; display: inline-flex; align-items: center; justify-content: center; font-size: 10px;" title="O'chirish">✕</span>
              </span>
            </div>
          </div>

          <div class="form-group">
            <label>Jadvaldan guruh qo'shish (qidirish):</label>
            <SearchableSelect 
              v-model="subjectModalSelectedGroup"
              :options="availableGroups"
              item-value="id"
              item-label="name"
              placeholder="Guruhni qidiring..."
              @change="handleGroupAdd"
            />
          </div>

          <div v-if="addedGroupsForMaruza0.length > 0" class="sep-badge-info" style="margin-top: 8px;">
            <span>📌 Qo'shilgan guruh (<b>{{ addedGroupsForMaruza0.map(g => g.rawName).join(', ') }}</b>) talabalar soni ushbu qatorga qo'shildi, uning qatorida esa <b>{{ activeTrainingType }}</b> soati 0 qilinadi.</span>
          </div>

          <div class="form-group">
            <label>Jami talabalar soni (xato bo'lsa to'g'rilashingiz mumkin):</label>
            <input type="text" v-model="subjectModalTalaba" class="modal-input" placeholder="Masalan: 12 yoki 12+12" />
          </div>

          <!-- Auditoriya hours fine tuning -->
          <div class="form-group hours-section" v-if="subjectModalRow">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;">
              <label style="margin: 0; font-weight: 600;">Auditoriya soatlari (moslashtirish):</label>
              <button type="button" class="btn-reset-hours" @click="resetModalHours" title="Soatlarni dastlabki formulaga tiklash">🔄 Formulaga tiklash</button>
            </div>
            <div class="hours-grid">
              <div class="hour-field" :class="{ highlighted: activeTrainingType === 'maruza' }">
                <span>Ma'ruza:</span>
                <input type="number" v-model.number="subjectModalHours.maruza" class="modal-input hour-inp" :placeholder="calc(subjectModalRow).audit.maruza" />
                <button type="button" class="btn-zero" @click="subjectModalHours.maruza = 0" title="Ma'ruzani 0 qilish">0</button>
              </div>
              <div class="hour-field" :class="{ highlighted: activeTrainingType === 'amaliy' }">
                <span>Amaliy:</span>
                <input type="number" v-model.number="subjectModalHours.amaliy" class="modal-input hour-inp" :placeholder="calc(subjectModalRow).audit.amaliy" />
                <button type="button" class="btn-zero" @click="subjectModalHours.amaliy = 0" title="Amaliyni 0 qilish">0</button>
              </div>
              <div class="hour-field" :class="{ highlighted: activeTrainingType === 'seminar' }">
                <span>Seminar:</span>
                <input type="number" v-model.number="subjectModalHours.seminar" class="modal-input hour-inp" :placeholder="calc(subjectModalRow).audit.seminar" />
                <button type="button" class="btn-zero" @click="subjectModalHours.seminar = 0" title="Seminarni 0 qilish">0</button>
              </div>
              <div class="hour-field" :class="{ highlighted: activeTrainingType === 'laboratoriya' }">
                <span>Lab:</span>
                <input type="number" v-model.number="subjectModalHours.laboratoriya" class="modal-input hour-inp" :placeholder="calc(subjectModalRow).audit.laboratoriya" />
                <button type="button" class="btn-zero" @click="subjectModalHours.laboratoriya = 0" title="Laboratoriyani 0 qilish">0</button>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn-clear" @click="closeSubjectModal">Bekor qilish</button>
          <button class="btn-confirm" @click="confirmSubjectModal" :disabled="isSavingModal">
            {{ isSavingModal ? 'Saqlanmoqda...' : 'Saqlash' }}
          </button>
        </div>
      </div>
    </div>

    <!-- O'chirish tasdiqlash modali -->
    <div v-if="deleteConfirmRow" class="modal-overlay" @click.self="deleteConfirmRow = null" style="z-index:9999;">
      <div class="modal-box" style="max-width:460px;">
        <div class="modal-header" style="background:#fee2e2; border-bottom:2px solid #fca5a5;">
          <h3 style="color:#991b1b; margin:0; font-size:16px;">⚠️ O'chirishni tasdiqlang</h3>
        </div>
        <div class="modal-body" style="padding:20px;">
          <p style="color:#374151; margin-bottom:16px; font-size:14px;">Quyidagi qatorni o'chirmoqchimisiz?</p>
          <table style="width:100%; font-size:13px; border-collapse:collapse;">
            <tr style="border-bottom:1px solid #e5e7eb;">
              <td style="padding:6px 8px; color:#6b7280; white-space:nowrap;">Fan nomi:</td>
              <td style="padding:6px 8px; font-weight:600; color:#111827;">{{ getSubjectNameDisplay(deleteConfirmRow) }}</td>
            </tr>
            <tr style="border-bottom:1px solid #e5e7eb;">
              <td style="padding:6px 8px; color:#6b7280; white-space:nowrap;">Curriculum:</td>
              <td style="padding:6px 8px; font-weight:600; color:#1d4ed8;">{{ deleteConfirmRow.curriculumName || '—' }}</td>
            </tr>
            <tr style="border-bottom:1px solid #e5e7eb;">
              <td style="padding:6px 8px; color:#6b7280; white-space:nowrap;">Guruh:</td>
              <td style="padding:6px 8px; font-weight:600; color:#111827;">{{ deleteConfirmRow.guruhNomi || '—' }}</td>
            </tr>
            <tr>
              <td style="padding:6px 8px; color:#6b7280; white-space:nowrap;">Semestr:</td>
              <td style="padding:6px 8px; font-weight:600; color:#111827;">{{ deleteConfirmRow.semester?.name || deleteConfirmRow.semestrTuri || '—' }}</td>
            </tr>
          </table>
        </div>
        <div class="modal-footer" style="gap:12px; padding:16px 20px; border-top:1px solid #fca5a5;">
          <button class="btn-clear" @click="deleteConfirmRow = null" style="flex:1;">Bekor qilish</button>
          <button @click="doDeleteRow" :disabled="isDeleting" style="flex:1; background:#dc2626; color:#fff; border:none; padding:9px 16px; border-radius:6px; font-weight:600; cursor:pointer;">
            {{ isDeleting ? "O'chirilmoqda..." : "✕ O'chirish" }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from "axios";
import SearchableSelect from "../components/SearchableSelect.vue";
import * as XLSX from "xlsx";

const API = process.env.VUE_APP_API_URL;

export default {
  name: "YuklamaJadval",
  components: {
    SearchableSelect,
  },
  watch: {
    subjectModalGuruh(newVal, oldVal) {
      if (!this.showSubjectModal) return;
      const oldGuruhs = (oldVal || "").split(',').map(s => s.trim()).filter(Boolean);
      const newGuruhs = (newVal || "").split(',').map(s => s.trim()).filter(Boolean);
      
      const removed = oldGuruhs.filter(g => !newGuruhs.includes(g));
      if (removed.length > 0) {
        let currentTotal = Number(this.subjectModalTalaba) || Number(this.subjectModalRow?.talabalarSoni) || 0;
        removed.forEach(gName => {
          const count = this.getGroupTalabaCount(gName);
          currentTotal = Math.max(0, currentTotal - count);
        });
        this.subjectModalTalaba = currentTotal.toString();
      }
    }
  },
  computed: {
    isSuperAdmin() {
      try {
        const u = localStorage.getItem("user");
        let parsed = {};
        if (u && u !== "undefined") {
          parsed = JSON.parse(u);
        }
        const role = (parsed.role || this.userRole || localStorage.getItem("role") || "").toLowerCase().trim();
        const name = (parsed.name || parsed.fullName || localStorage.getItem("name") || "").toLowerCase().trim();
        return ["admin", "super admin", "superadmin", "administrator"].includes(role) || name.includes("super admin");
      } catch (e) {
        return false;
      }
    },
    displayRows() {
      const getGroupBase = (str) => {
        if (!str) return "";
        let clean = str.replace(/\b(19|20)\d{2}\b/g, '');
        return clean.replace(/[^a-z0-9]/gi, '').toLowerCase();
      };

      const isKurs = (r) => {
        let n = "";
        if (typeof r?.subject === 'object') n = r.subject?.name || "";
        if (!n) n = r?.subjectName || r?.fanNomi || r?.name || "";
        return /kurs\s*(ishi|loyihasi?)/i.test(n);
      };

      const getSubjectBase = (r) => {
        if (!r) return "";
        let name = "";
        if (r.subject) {
          if (typeof r.subject === 'object') {
            const sObj = Array.isArray(r.subject) ? r.subject[0] : r.subject;
            name = sObj?.name || sObj?.title || sObj?.subjectName || "";
          } else if (typeof r.subject === 'string' && !/^[0-9a-fA-F]{24}$/.test(r.subject)) {
            name = r.subject;
          }
        }
        if (!name && r.subjectDetails) {
          const sdObj = Array.isArray(r.subjectDetails) ? r.subjectDetails[0] : r.subjectDetails;
          name = sdObj?.name || sdObj?.title || sdObj?.subjectName || "";
        }
        if (!name && r.subjectName) name = r.subjectName;
        if (!name && r.customSubject) name = r.customSubject;
        if (!name && r.name) name = r.name;
        if (!name && r.fanNomi) name = r.fanNomi;

        const base = (name || "")
          .replace(/\u0430/g,'a').replace(/\u0410/g,'A') // Cyrillic а→a А→A
          .replace(/\u0435/g,'e').replace(/\u0415/g,'E') // Cyrillic е→e Е→E
          .replace(/\u043E/g,'o').replace(/\u041E/g,'O') // Cyrillic о→o О→O
          .replace(/\u0441/g,'c').replace(/\u0421/g,'C') // Cyrillic с→c С→C
          .replace(/\u0440/g,'r').replace(/\u0420/g,'R') // Cyrillic р→r Р→R
          .replace(/\u0445/g,'x').replace(/\u0425/g,'X') // Cyrillic х→x Х→X
          .replace(/\|.*/g, '')
          .replace(/\((?!kurs)[A-Z0-9\s-]+\)/gi, '')
          .replace(/\b[A-Z]{2,6}\d{2,6}\b/gi, '')
          .replace(/[^a-z]/gi, '')
          .toLowerCase();

        return isKurs(r) ? `${base}_kurs` : base;
      };

      const sameSubject = (r1, r2) => {
        if (!r1 || !r2) return false;
        if (isKurs(r1) !== isKurs(r2)) return false;

        // Try ID match first (most reliable)
        const id1 = typeof r1.subject === 'object' ? (r1.subject?._id || r1.subject?.id) : (typeof r1.subject === 'string' ? r1.subject : "");
        const id2 = typeof r2.subject === 'object' ? (r2.subject?._id || r2.subject?.id) : (typeof r2.subject === 'string' ? r2.subject : "");
        if (id1 && id2 && String(id1) === String(id2)) return true;

        // Try code match
        const code1 = typeof r1.subject === 'object' ? (r1.subject?.code || "") : (r1.subjectCode || "");
        const code2 = typeof r2.subject === 'object' ? (r2.subject?.code || "") : (r2.subjectCode || "");
        if (code1 && code2 && String(code1).toLowerCase().trim() === String(code2).toLowerCase().trim()) return true;

        // Fall back to name comparison
        const sBase1 = getSubjectBase(r1);
        const sBase2 = getSubjectBase(r2);
        if (sBase1 && sBase2) return sBase1 === sBase2;

        return false;
      };

      const sameSemester = (r1, r2) => {
        const sem1 = r1.semester?.code || r1.semestrTuri;
        const sem2 = r2.semester?.code || r2.semestrTuri;
        if (!sem1 || !sem2) return true;
        return String(sem1) === String(sem2);
      };

      const sameEducationForm = (r1, r2) => {
        const f1 = r1.educationForm?.code || r1.educationForm?.name;
        const f2 = r2.educationForm?.code || r2.educationForm?.name;
        if (!f1 || !f2) return true;
        return String(f1) === String(f2);
      };

      const combinedRows = [];

      this.rows.forEach(r => {
        if (!r.guruhNomi) return;
        const rawList = this.splitGroupNames(r.guruhNomi);
        if (rawList.length > 1) {
          const subjectBase = getSubjectBase(r);
          const baseGroups = rawList.map(g => getGroupBase(g)).filter(Boolean);
          combinedRows.push({
            row: r,
            subjectBase,
            baseGroupsSet: new Set(baseGroups),
            baseGroupsList: baseGroups
          });
        }
      });

      // Dynamically mark single-group rows that are secondary attached groups of a stream
      this.rows.forEach(r => {
        if (!r.guruhNomi) return;
        const rList = this.splitGroupNames(r.guruhNomi);
        
        if (rList.length > 1) {
          r.isPrimaryStream = true;
          r._isSecondaryInStream = false;
          delete r.isSyntheticGroup;
          return;
        } else {
          delete r.isPrimaryStream;
        }

        const isSecondaryInStream = combinedRows.some(c => {
          if (c.row === r) return false;
          if (!sameSubject(r, c.row)) return false;
          if (!sameSemester(r, c.row)) return false;
          if (!sameEducationForm(r, c.row)) return false;
          const cList = this.splitGroupNames(c.row.guruhNomi);
          for (let i = 1; i < cList.length; i++) {
            if (getGroupBase(cList[i]) === getGroupBase(rList[0])) return true;
          }
          return false;
        });

        r._isSecondaryInStream = isSecondaryInStream;

        if (isSecondaryInStream) {
          r.isSyntheticGroup = true;
        } else if (r.isSyntheticGroup) {
          delete r.isSyntheticGroup;
        }
      });

      const filtered = this.rows.filter(r => {
        const hasPositiveHours = (
          r.maruza > 0 || r.seminar > 0 || r.amaliy > 0 || r.laboratoriya > 0 ||
          r.kursIshi > 0 || r.kursIshiHimoya > 0 || r.oraliq > 0 || r.yakuniy > 0 || r.boshqaTashqari > 0
        );
        if (r.isSyntheticGroup || r._isSecondaryInStream || hasPositiveHours || r.isCustom) return true;
        if (!r.guruhNomi) return true;
        const rRawList = this.splitGroupNames(r.guruhNomi);
        if (rRawList.length === 0) return true;

        const rBaseGroups = rRawList.map(g => getGroupBase(g)).filter(Boolean);

        const isMergedIntoAnotherRow = combinedRows.some(c => {
          if (c.row === r) return false;

          if (!sameSemester(r, c.row)) return false;
          if (!sameEducationForm(r, c.row)) return false;
          if (!sameSubject(r, c.row)) return false;

          return rBaseGroups.every(rgBase => {
            if (c.baseGroupsSet.has(rgBase)) return true;
            return c.baseGroupsList.some(cgBase => {
              return rgBase && cgBase && rgBase === cgBase;
            });
          });
        });

        return !isMergedIntoAnotherRow;
      });

      const attachedGroupMap = [];
      combinedRows.forEach(c => {
        const rawList = [...new Set(this.splitGroupNames(c.row.guruhNomi).map(s => s.trim()))].filter(Boolean);
        for (let i = 1; i < rawList.length; i++) {
          attachedGroupMap.push({
            groupName: rawList[i],
            parentRow: c.row
          });
        }
      });

      const injectedRows = [];
      const getClean = (s) => (s || "").replace(/\b(19|20)\d{2}\b/g, '').replace(/sirtqi|kunduzgi|kechki/gi, '').replace(/[^a-z0-9]/gi, '').toLowerCase();

      attachedGroupMap.forEach(({ groupName, parentRow }) => {
        const gClean = getClean(groupName);

        const hasExisting = filtered.some(r => {
          if (!r.guruhNomi) return false;
          if (!sameSubject(r, parentRow)) return false;
          if (!sameSemester(r, parentRow)) return false;
          if (!sameEducationForm(r, parentRow)) return false;
          const rList = this.splitGroupNames(r.guruhNomi);
          if (rList.length > 1) return false;
          return getClean(r.guruhNomi) === gClean || r.guruhNomi.trim() === groupName.trim();
        });

        const alreadyInjected = injectedRows.some(inj =>
          sameSubject(inj, parentRow) &&
          sameSemester(inj, parentRow) &&
          getClean(inj.guruhNomi) === gClean
        );

        if (!hasExisting && !alreadyInjected) {
          let sepTalaba = 20;
          const gCleanCheck = getClean(groupName);

          const exactSingleRow = this.rows.find(r => {
            if (!r.guruhNomi) return false;
            if (!sameSemester(r, parentRow)) return false;
            if (!sameEducationForm(r, parentRow)) return false;
            const parts = this.splitGroupNames(r.guruhNomi);
            if (parts.length !== 1) return false;
            return getClean(parts[0]) === gCleanCheck || parts[0].trim() === groupName.trim();
          });

          if (exactSingleRow && exactSingleRow.talabalarSoni) {
            sepTalaba = exactSingleRow.talabalarSoni;
          } else {
            const anyRowWithGroup = this.rows.find(r =>
              r.guruhNomi &&
              this.splitGroupNames(r.guruhNomi).some(g => getClean(g) === gCleanCheck)
            );
            if (anyRowWithGroup && anyRowWithGroup.talabalarSoni) {
              const parts = this.splitGroupNames(anyRowWithGroup.guruhNomi);
              if (parts.length === 1) {
                sepTalaba = anyRowWithGroup.talabalarSoni;
              } else {
                // this.availableGroups ishlatmaymiz — tsiklli bog'liqlik oldini olish uchun
                // to'g'ridan-to'g'ri rows dan bitta guruhli qatorni qidirish
                const singleGroupRow = this.rows.find(rx => {
                  if (!rx.guruhNomi || !rx.talabalarSoni) return false;
                  const rxParts = this.splitGroupNames(rx.guruhNomi);
                  return rxParts.length === 1 && getClean(rxParts[0]) === gCleanCheck;
                });
                if (singleGroupRow && singleGroupRow.talabalarSoni > 0 && singleGroupRow.talabalarSoni < anyRowWithGroup.talabalarSoni) {
                  sepTalaba = singleGroupRow.talabalarSoni;
                } else {
                  sepTalaba = Math.round(anyRowWithGroup.talabalarSoni / (parts.length || 2));
                }
              }
            }
          }

          let synthSpecialty = null;
          let synthCustomSpecialty = "";
          const groupSingleRow = this.rows.find(r => {
            if (!r.guruhNomi) return false;
            const parts = this.splitGroupNames(r.guruhNomi);
            return parts.length === 1 && (getClean(parts[0]) === gCleanCheck || parts[0].trim() === groupName.trim());
          });
          if (groupSingleRow) {
            synthSpecialty = groupSingleRow.specialty;
            synthCustomSpecialty = groupSingleRow.customSpecialty || "";
          } else {
            synthSpecialty = parentRow.specialty;
            synthCustomSpecialty = parentRow.customSpecialty || "";
          }

          injectedRows.push({
            _id: `synth_${parentRow._id || parentRow.id}_${gClean}`,
            departmentId: parentRow.departmentId,
            oquvYili: parentRow.oquvYili,
            semestrTuri: parentRow.semestrTuri,
            semester: parentRow.semester,
            subject: parentRow.subject,
            subjectDetails: parentRow.subjectDetails,
            subjectExamTypes: parentRow.subjectExamTypes,
            haftaSoni: parentRow.haftaSoni,
            specialty: synthSpecialty,
            customSpecialty: synthCustomSpecialty,
            guruhNomi: groupName,
            talabalarSoni: sepTalaba,
            maruza: 0,
            oqimSoni: 1,
            isSyntheticGroup: true
          });
        }
      });

      // Dublikatlarni oldini olish: bir xil _id bo'lgan qatorlarni filtrlaymiz
      const seenIds = new Set();
      const seenGroupSubjectKeys = new Set();
      const combined = [...filtered, ...injectedRows];
      let resultRows = combined.filter(row => {
        const id = row._id || row.itemId;
        if (id) {
          if (seenIds.has(String(id))) return false;
          seenIds.add(String(id));
        }
        if (row.isSyntheticGroup && row.guruhNomi) {
          const sBase = getSubjectBase(row);
          const sem = row.semester?.code || row.semestrTuri || "";
          const gClean = getClean(row.guruhNomi);
          const key = `${sBase}_${sem}_${gClean}`;
          if (seenGroupSubjectKeys.has(key)) return false;
          seenGroupSubjectKeys.add(key);
        }
        return true;
      });

      if (this.filters.kurs !== '' && this.filters.kurs !== null && this.filters.kurs !== undefined) {
        resultRows = resultRows.filter(row => {
          const k = String(row.customKurs || this.getKurs(row)).trim();
          return k === String(this.filters.kurs).trim();
        });
      }

      return resultRows;
    },
    availableCourses() {
      const set = new Set();
      (this.rows || []).forEach(row => {
        const k = row.customKurs || this.getKurs(row);
        if (k && k !== "—" && !isNaN(Number(k))) {
          set.add(Number(k));
        }
      });
      [1, 2, 3, 4].forEach(n => set.add(n));
      return Array.from(set).sort((a, b) => a - b);
    },
    totals() {
      const keys = [
        "kursIshi", "kursIshiHimoya", "kursLoyihasi", "kursLoyihasiHimoya",
        "hisobChizma", "oraliq", "yakuniy", "amaliyotInd", "amaliyotUni",
        "amaliyotTash", "bmiRahbar", "bmiDak", "magistr", "mdIti", "mdDak",
        "doktorant", "yadaYozmaGuruh", "yadaYozmaTalaba", "yadaTest",
        "ochiqLeksiya", "malakaOshirish", "jamiAudit", "jamiTashqari", "jami"
      ];
      const result = {
        talabalarSoni: 0, oqimSoni: 0, guruhSoni: 0, kichikGuruhSoni: 0,
        maruza: 0, amaliy: 0, seminar: 0, laboratoriya: 0,
      };
      keys.forEach(k => result[k] = 0);
      this.displayRows.forEach(row => {
        const c = this.calc(row);
        result.talabalarSoni  += row.talabalarSoni || 0;
        result.oqimSoni       += row.oqimSoni || row.oqim || 0;
        result.guruhSoni      += row.guruhSoni || 0;
        result.kichikGuruhSoni+= row.kichikGuruhSoni || 0;
        result.maruza         += c.audit.maruza;
        result.amaliy         += c.audit.amaliy;
        result.seminar        += c.audit.seminar;
        result.laboratoriya   += c.audit.laboratoriya;
        keys.forEach(k => result[k] = Math.round(result[k] + (c[k] || 0)));
      });
      // round final
      Object.keys(result).forEach(k => {
        result[k] = Math.round(result[k]);
      });
      return result;
    },
    isEditable() {
      return this.userRole === 'admin' || this.userRole === 'Super Admin';
    },
    isSuperAdmin() {
      return this.userRole === 'Super Admin';
    },
    availableGroups() {
      const groups = [];
      const seen = new Set();
      const currentModalGroups = this.parsedGuruhs || [];

      const getNorm = (s) => (s || "").toString().replace(/[^a-z0-9]/gi, '').toLowerCase();
      const getCode = (s) => {
        const clean = (s || "").toString().replace(/\(20\d\d-20\d\d\)/g, '').replace(/20\d\d-20\d\d/g, '');
        const m = clean.match(/([a-zA-Z]{2,6}\s*\d{1,4}[a-zA-Z]?|\d{1,3}-\d{2,4})/);
        return m ? m[0].replace(/\s+/g, '').toUpperCase() : "";
      };

      const isAlreadyAdded = (name, shortName, currName) => {
        const nNorm = getNorm(name);
        const sNorm = getNorm(shortName);
        const cNorm = getNorm(currName);
        const nCode = getCode(name) || getCode(shortName) || getCode(currName);

        return currentModalGroups.some(mg => {
          const mgNorm = getNorm(mg);
          const mgCode = getCode(mg);
          if (nNorm && mgNorm && nNorm === mgNorm) return true;
          if (sNorm && mgNorm && sNorm === mgNorm) return true;
          if (cNorm && mgNorm && cNorm === mgNorm) return true;
          if (nCode && mgCode && nCode === mgCode) return true;
          return false;
        });
      };

      // 1. Precompute group counts and full names mapping from rows
      const groupCounts = {};
      const groupToFullNameMap = {};
      const groupToCurriculumMap = {};

      this.rows.forEach(r => {
        const currName = r.curriculumName ? r.curriculumName.trim() : "";
        const gName = r.guruhNomi ? r.guruhNomi.trim() : "";
        const fullN = this.getFullGuruhNomi(r);
        const count = Number(r.talabalarSoni) || 0;

        if (gName && gName !== "—") {
          const parts = this.splitGroupNames(gName);
          if (parts.length === 1) {
            const p = parts[0];
            if (currName && !groupToCurriculumMap[p]) groupToCurriculumMap[p] = currName;
            if (fullN && fullN !== "—" && !groupToFullNameMap[p]) groupToFullNameMap[p] = fullN;
            if (count > 0) {
              groupCounts[p] = count;
              groupCounts[getNorm(p)] = count;
            }
          }
        }
        if (currName) {
          if (!groupToFullNameMap[currName]) groupToFullNameMap[currName] = currName;
          if (count > 0) {
            groupCounts[currName] = count;
            groupCounts[getNorm(currName)] = count;
          }
        }
        if (fullN && fullN !== "—") {
          if (count > 0) {
            groupCounts[fullN] = count;
            groupCounts[getNorm(fullN)] = count;
          }
        }
      });

      // 2. Extract candidate groups from rows
      this.rows.forEach(r => {
        const currName = r.curriculumName ? r.curriculumName.trim() : "";
        const gName = r.guruhNomi ? r.guruhNomi.trim() : "";
        if ((!gName || gName === "—") && !currName) return;

        const spName = r.specialty?.name || r.customSpecialty || "";
        const fullRowName = this.getFullGuruhNomi(r);

        // A) If row has curriculumName, add it as a primary long-name group option
        if (currName) {
          const key = getNorm(currName);
          if (!seen.has(key)) {
            seen.add(key);
            const shortName = (gName && gName !== "—") ? gName : "";
            if (shortName) seen.add(getNorm(shortName));

            const exactCount = groupCounts[currName] || (shortName ? groupCounts[shortName] : null) || (r.talabalarSoni || 0);
            const alreadyAdded = isAlreadyAdded(currName, shortName, currName);
            const displayName = fullRowName && fullRowName !== "—" ? fullRowName : currName;

            groups.push({
              id: displayName,
              name: `${displayName} (${exactCount} talaba)${alreadyAdded ? ' ✓' : ''}`,
              rawName: displayName,
              shortName: shortName || displayName,
              curriculumName: currName,
              talaba: exactCount,
              specialtyName: spName,
              searchTerms: `${displayName} ${shortName} ${currName} ${spName}`,
              alreadyAdded
            });
          }
        }

        // B) Also process each individual group in r.guruhNomi
        if (gName && gName !== "—") {
          const gNames = this.splitGroupNames(gName);
          gNames.forEach(gName => {
            const gKey = getNorm(gName);
            if (!seen.has(gKey)) {
              seen.add(gKey);

              const mappedCurr = groupToCurriculumMap[gName] || (gNames.length === 1 ? currName : "");
              const mappedFull = groupToFullNameMap[gName] || (mappedCurr ? mappedCurr : this.getFullGuruhNomiFor(gName, spName));
              const primaryName = mappedFull && mappedFull !== "—" ? mappedFull : gName;

              const exactCount = groupCounts[gName] || (mappedCurr ? groupCounts[mappedCurr] : null) || groupCounts[primaryName] || (r.talabalarSoni || 0);
              const alreadyAdded = isAlreadyAdded(primaryName, gName, mappedCurr);

              groups.push({
                id: primaryName,
                name: `${primaryName} (${exactCount} talaba)${alreadyAdded ? ' ✓' : ''}`,
                rawName: primaryName,
                shortName: gName,
                curriculumName: mappedCurr,
                talaba: exactCount,
                specialtyName: spName,
                searchTerms: `${primaryName} ${gName} ${mappedCurr} ${spName}`,
                alreadyAdded
              });
            }
          });
        }
      });

      // 3. User manually entered groups not in rows
      currentModalGroups.forEach(gName => {
        if (!gName) return;
        const gKey = getNorm(gName);
        if (!seen.has(gKey)) {
          seen.add(gKey);
          const talaba = this.subjectModalRow?.talabalarSoni || groupCounts[gName] || 0;
          const spName = this.subjectModalRow?.specialty?.name || this.subjectModalRow?.customSpecialty || "";
          const fullN = this.getFullGuruhNomiFor(gName, spName);
          const displayName = fullN && fullN !== "—" ? fullN : gName;
          groups.push({
            id: displayName,
            name: `${displayName} (${talaba} talaba) ✓`,
            rawName: displayName,
            shortName: gName,
            curriculumName: "",
            talaba: talaba,
            specialtyName: spName,
            searchTerms: `${displayName} ${gName} ${spName}`,
            alreadyAdded: true
          });
        }
      });

      return groups;
    },
    parsedGuruhs() {
      if (!this.subjectModalGuruh) return [];
      return this.splitGroupNames(this.subjectModalGuruh);
    },
    guruhSpecialtyMap() {
      const map = {};
      const normalize = (str) => (str || "").replace(/\s+/g, ' ').trim().toLowerCase();
      this.rows.forEach(r => {
        const sp = r.specialty?.name || r.customSpecialty || "";
        if (!sp) return;
        if (r.guruhNomi) {
          const groups = this.splitGroupNames(r.guruhNomi);
          groups.forEach(g => {
            const normKey = normalize(g);
            if (!map[g] || groups.length === 1) map[g] = sp;
            if (!map[normKey] || groups.length === 1) map[normKey] = sp;
          });
        }
        if (r.curriculumName) {
          const cName = r.curriculumName.trim();
          map[cName] = sp;
          map[normalize(cName)] = sp;
        }
        const fullN = this.getFullGuruhNomi(r);
        if (fullN && fullN !== "—") {
          map[fullN] = sp;
          map[normalize(fullN)] = sp;
        }
      });
      return map;
    }
  },
  data() {
    return {
      userRole: "",
      loading: false,
      rows: [],
      total: 0,
      currentPage: 1,
      pageCount: 1,
      limit: 5000,
      kafedraName: "",
      filters: {
        departmentId: "",
        oquvYili: "",
        semestrTuri: "",
        semester: "",
        kurs: "",
      },
      showKursDropdown: false,
      semesters: ["11", "12", "13", "14", "15", "16", "17", "18"],
      showModal: false,
      modalTitle: "",
      editValue: "",
      inputType: "number",
      editingRow: null,
      editingField: "",
      isTashqariEdit: false,
      tashqariConfig: {},
      tashqariInputVal: 0,
      tashqariSelectVal: null,
      tashqariHafta: 0,
      tashqariGuruh: 0,
      selectedCustomWorkload: "",
      addingCustom: false,
      syncing: false,
      showSubjectModal: false,
      subjectModalRow: null,
      subjectModalGuruh: "",
      subjectModalTalaba: "",
      subjectModalSpecialty: "",
      subjectModalSelectedGroup: "",
      groupSpecialtyMap: {},
      activeTrainingType: "maruza",
      groupAddMode: "maruza0",
      groupAddStrategy: "separate",
      selectedGroupForSeparate: null,
      addedGroupsForMaruza0: [],
      removedGroupsFromSubject: [],
      knownGroupCounts: {},
      subjectModalHours: {
        maruza: null,
        amaliy: null,
        seminar: null,
        laboratoriya: null
      },
      isSavingModal: false,
      deleteConfirmRow: null,
      isDeleting: false,
      buttonsConfig: {
        showSync: true,
        showBackup: true,
      },
      showConfigModal: false,
      showBackupDropdown: false,
      backupLoading: false,
    };
  },
  mounted() {
    try {
      const data = localStorage.getItem("user");
      if (data && data !== "undefined") {
        this.userRole = JSON.parse(data).role || "";
      }
    } catch (e) { /* ignore */ }

    const q = this.$route.query;
    this.filters.departmentId = q.departmentId || "";
    this.filters.oquvYili = q.oquvYili || "2026-2027";
    this.filters.semestrTuri = q.semestrTuri || "";
    this.filters.semester = q.semester || "";
    this.filters.kurs = q.kurs || "";
    this.kafedraName = q.name || "";

    if (this.filters.departmentId) {
      this.fetchData();
    }

    this.loadButtonsConfig();
    window.addEventListener('click', this.handleOutsideClick);
  },
  beforeUnmount() {
    window.removeEventListener('click', this.handleOutsideClick);
  },
  methods: {
    getSubjectNameDisplay(r) {
      if (!r) return "";
      let name = "";
      if (r.subject) {
        if (typeof r.subject === 'object') {
          const sObj = Array.isArray(r.subject) ? r.subject[0] : r.subject;
          name = sObj?.name || sObj?.title || sObj?.subjectName || "";
        } else if (typeof r.subject === 'string' && !/^[0-9a-fA-F]{24}$/.test(r.subject) && isNaN(Number(r.subject))) {
          name = r.subject;
        }
      }
      if (!name && r.subjectDetails) {
        const sdObj = Array.isArray(r.subjectDetails) ? r.subjectDetails[0] : r.subjectDetails;
        name = sdObj?.name || sdObj?.title || sdObj?.subjectName || "";
      }
      if (!name && r.subjectName) name = r.subjectName;
      if (!name && r.customSubject) name = r.customSubject;
      if (!name && r.name) name = r.name;
      if (!name && r.fanNomi) name = r.fanNomi;

      if (!name && (r.curriculumId || r.subject)) {
        const subId = typeof r.subject === 'object' ? (r.subject?.id || r.subject?._id) : r.subject;
        const semCode = r.semester?.code || "";
        const match = (this.rows || []).find(other => {
          if (other === r) return false;
          let oName = "";
          if (other.subject && typeof other.subject === 'object') {
            const sObj = Array.isArray(other.subject) ? other.subject[0] : other.subject;
            oName = sObj?.name || sObj?.title || sObj?.subjectName || "";
          }
          if (!oName && other.subjectName) oName = other.subjectName;
          if (!oName) return false;

          if (subId && other.subject && typeof other.subject === 'object' && String(other.subject.id || other.subject._id) === String(subId)) {
            return true;
          }
          if (r.curriculumId && other.curriculumId && String(r.curriculumId) === String(other.curriculumId)) {
            if (semCode && other.semester?.code && String(semCode) === String(other.semester.code)) {
              return true;
            }
          }
          return false;
        });
        if (match) {
          if (match.subject && typeof match.subject === 'object') {
            const sObj = Array.isArray(match.subject) ? match.subject[0] : match.subject;
            name = sObj?.name || sObj?.title || sObj?.subjectName || "";
          }
          if (!name && match.subjectName) name = match.subjectName;
        }
      }

      return name || "—";
    },
    getSubjectCodeDisplay(r) {
      if (!r) return "";
      let code = "";
      if (r.subject) {
        if (typeof r.subject === 'object') {
          const sObj = Array.isArray(r.subject) ? r.subject[0] : r.subject;
          code = sObj?.code || "";
        }
      }
      if (!code && r.subjectDetails) {
        const sdObj = Array.isArray(r.subjectDetails) ? r.subjectDetails[0] : r.subjectDetails;
        code = sdObj?.code || "";
      }
      if (!code && r.subjectCode) code = r.subjectCode;
      return code;
    },
    getFullGuruhNomiFor(gName, spName) {
      gName = String(gName || "").trim();
      if (!gName) return "—";
      
      const normalized = gName;
      if (/\(\d{4}-\d{4}\)/.test(normalized)) {
        return normalized;
      }
      
      if (!/^\d+-\d+/.test(normalized)) {
        return normalized;
      }
      
      const match = normalized.match(/(\d{2})/);
      if (!match) return normalized;
      
      const year2 = parseInt(match[1], 10);
      const fullYear = 2000 + year2;
      const yearStr = '(' + fullYear + '-' + (fullYear + 1) + ')';
      
      let prefix = '';
      if (spName && spName.toLowerCase().includes('konchilik elektr mexanikasi')) {
        prefix = 'KEM';
      } else if (spName && spName.toLowerCase().includes('transport vositalari muhandisligi')) {
        prefix = 'TVM';
      } else {
        const getShortSpecialty = (name) => {
          if (!name) return "";
          const words = name.replace(/\(.*\)/g, '').split(/[\s\-]+/);
          let short = words.map((w) => w.charAt(0).toUpperCase()).join('');
          return short;
        };
        prefix = getShortSpecialty(spName);
        if (!prefix) prefix = normalized.replace(/\d.*$/, '').trim();
      }
      
      let suffix = '';
      if (normalized.toLowerCase().includes('sirtqi')) suffix = ' sirtqi';
      if (normalized.toLowerCase().includes('kechki')) suffix = ' kechki';
      
      return `${prefix} ${yearStr}${suffix}`.trim();
    },

    getFullGuruhNomi(row) {
      if (!row) return "—";
      
      const gName = String(row.guruhNomi || "").trim();
      
      if (gName.includes(',')) {
        return gName;
      }
      
      if (row.curriculumName && row.curriculumName.trim() !== '') {
          return row.curriculumName.trim();
      }
      
      if (!gName || gName === "—") return "—";
      
      if (/^\d+-/.test(gName)) {
        const spName = (this.getRowSpecialty ? this.getRowSpecialty(row) : (row.customSpecialty || row.specialty?.name || ""));
        return this.getFullGuruhNomiFor(gName, spName);
      }
      
      return gName;
    },

    splitGroupNames(str) {
      if (!str) return [];
      const result = [];
      let current = '';
      let depth = 0;
      const s = str.toString();
      for (let i = 0; i < s.length; i++) {
        const char = s[i];
        if (char === '(') depth++;
        else if (char === ')') depth = Math.max(0, depth - 1);
        
        if (char === ',' && depth === 0) {
          if (current.trim()) result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      if (current.trim()) result.push(current.trim());
      return result;
    },
    async fetchData() {
      this.loading = true;
      this.rows = [];
      try {
        const res = await axios.get(`${API}/kafedra-yuklamalar`, {
          params: {
            departmentId: this.filters.departmentId,
            oquvYili: this.filters.oquvYili,
            semestrTuri: this.filters.semestrTuri,
            semester: this.filters.semester,
            page: this.currentPage,
            limit: this.limit,
          },
        });
        this.rows = res.data.data || [];
        this.rows.forEach(r => {
          if (r.guruhNomi) {
            const pCount = r.guruhNomi.split(',').map(s => s.trim()).filter(Boolean).length;
            if (pCount > 1) {
              if (!r.guruhSoni) {
                r.guruhSoni = pCount;
              }
              if (!r.kichikGuruhSoni || r.kichikGuruhSoni < pCount) {
                r.kichikGuruhSoni = pCount;
              }
            }
          }
        });
        this.total = res.data.total || 0;
        this.pageCount = Math.ceil(this.total / this.limit) || 1;

        this.resolveAllGroupCounts();
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    resolveAllGroupCounts() {
      const counts = {};
      const getClean = (s) => (s || "").replace(/\b(19|20)\d{2}\b/g, '').replace(/sirtqi|kunduzgi|kechki/gi, '').replace(/[^a-z0-9]/gi, '').toLowerCase();

      this.rows.forEach(r => {
        if (!r.talabalarSoni) return;
        const count = Number(r.talabalarSoni);
        if (count <= 0) return;

        if (r.guruhNomi) {
          const parts = r.guruhNomi.split(',').map(s => s.trim()).filter(Boolean);
          if (parts.length === 1) {
            const name = parts[0];
            const cleanKey = getClean(name);
            counts[name] = count;
            counts[cleanKey] = count;
          }
        }
        if (r.curriculumName && r.curriculumName.trim()) {
          const cName = r.curriculumName.trim();
          counts[cName] = count;
          counts[getClean(cName)] = count;
        }
        const fullN = this.getFullGuruhNomi(r);
        if (fullN && fullN !== "—") {
          counts[fullN] = count;
          counts[getClean(fullN)] = count;
        }
      });

      this.rows.forEach(r => {
        if (!r.guruhNomi || !r.talabalarSoni) return;
        const parts = r.guruhNomi.split(',').map(s => s.trim()).filter(Boolean);
        if (parts.length > 1) {
          const total = Number(r.talabalarSoni);
          let knownSum = 0;
          let unknownGroup = null;
          let unknownCount = 0;

          parts.forEach(g => {
            const gClean = getClean(g);
            if (counts[g] || counts[gClean]) {
              knownSum += counts[g] || counts[gClean];
            } else {
              unknownCount++;
              unknownGroup = g;
            }
          });

          if (unknownCount === 1 && unknownGroup && total > knownSum) {
            const deduced = total - knownSum;
            counts[unknownGroup] = deduced;
            counts[getClean(unknownGroup)] = deduced;
          }
        }
      });

      this.knownGroupCounts = { ...this.knownGroupCounts, ...counts };

      // Ensure all secondary attached groups for the same subject get maruza = 0, oraliq = 0, yakuniy = 0
      const getSubjectBase = (r) => {
        if (!r) return "";
        let name = "";
        if (r.subject && typeof r.subject === 'object') {
          name = r.subject.name || r.subject.title || r.subject.subjectName || "";
        }
        if (!name && r.subjectDetails) {
          name = r.subjectDetails.name || r.subjectDetails.title || r.subjectDetails.subjectName || "";
        }
        if (!name && r.subjectName) name = r.subjectName;
        if (!name && r.customSubject) name = r.customSubject;
        if (!name && r.name) name = r.name;
        if (!name && r.fanNomi) name = r.fanNomi;

        if (!name && typeof r.subject === 'string') {
          if (!/^[0-9a-fA-F]{24}$/.test(r.subject)) {
            name = r.subject;
          }
        }

        return (name || "")
          .replace(/\u0430/g,'a').replace(/\u0410/g,'A')
          .replace(/\u0435/g,'e').replace(/\u0415/g,'E')
          .replace(/\u043E/g,'o').replace(/\u041E/g,'O')
          .replace(/\u0441/g,'c').replace(/\u0421/g,'C')
          .replace(/\u0440/g,'r').replace(/\u0420/g,'R')
          .replace(/\u0445/g,'x').replace(/\u0425/g,'X')
          .replace(/[^a-z0-9]/gi, '').toLowerCase();
      };

      const sameSubject = (r1, r2) => {
        if (!r1 || !r2) return false;
        const id1 = typeof r1.subject === 'object' ? (r1.subject?._id || r1.subject?.id) : (typeof r1.subject === 'string' ? r1.subject : "");
        const id2 = typeof r2.subject === 'object' ? (r2.subject?._id || r2.subject?.id) : (typeof r2.subject === 'string' ? r2.subject : "");
        if (id1 && id2 && String(id1) === String(id2)) return true;

        const code1 = typeof r1.subject === 'object' ? (r1.subject?.code || "") : (r1.subjectCode || "");
        const code2 = typeof r2.subject === 'object' ? (r2.subject?.code || "") : (r2.subjectCode || "");
        if (code1 && code2 && String(code1).toLowerCase().trim() === String(code2).toLowerCase().trim()) return true;

        if (r1.curriculumId && r2.curriculumId && String(r1.curriculumId) === String(r2.curriculumId)) {
          const sem1 = r1.semester?.code || r1.semestrTuri || "";
          const sem2 = r2.semester?.code || r2.semestrTuri || "";
          if (!sem1 || !sem2 || String(sem1) === String(sem2)) return true;
        }

        const sBase1 = getSubjectBase(r1);
        const sBase2 = getSubjectBase(r2);
        if (sBase1 && sBase2 && sBase1 === sBase2) return true;

        return false;
      };

      this.rows.forEach(row => {
        if (!row.guruhNomi) return;
        const parts = row.guruhNomi.split(',').map(s => s.trim()).filter(Boolean);
        if (parts.length > 1) {
          for (let i = 1; i < parts.length; i++) {
            const secClean = getClean(parts[i]);
            const secRaw = parts[i].trim();
            const childRow = this.rows.find(r => {
              if (r === row) return false;
              if (!r.guruhNomi && !r.curriculumName) return false;
              const rParts = (r.guruhNomi || "").split(',').map(s => s.trim()).filter(Boolean);
              if (rParts.length > 1) return false;

              const rGuruh = rParts[0] || "";
              const rClean = getClean(rGuruh);
              const rCurr = (r.curriculumName || "").trim();
              const rCurrClean = getClean(rCurr);
              const rFull = (this.getFullGuruhNomi(r) || "").trim();
              const rFullClean = getClean(rFull);

              const nameMatch = 
                (rClean && rClean === secClean) ||
                (rGuruh && rGuruh === secRaw) ||
                (rCurrClean && rCurrClean === secClean) ||
                (rCurr && rCurr === secRaw) ||
                (rFullClean && rFullClean === secClean) ||
                (rFull && rFull === secRaw);

              if (!nameMatch) return false;
              return sameSubject(row, r);
            });
            if (childRow) {
              childRow._isSecondaryInStream = true;
              childRow.isSyntheticGroup = true;
            }
          }
        }
      });
    },

    getKurs(row) {
      if (!row) return "—";
      if (row.customKurs) return row.customKurs;
      if (row.kurs) return row.kurs;
      const code = Number(row.semester?.code || row.semesterCode);
      if (!code) return "—";
      if (code >= 11) return Math.ceil((code - 10) / 2);
      if (code >= 1 && code <= 10) return Math.ceil(code / 2);
      return "—";
    },
    toggleKursDropdown() {
      this.showKursDropdown = !this.showKursDropdown;
    },
    setKurs(value) {
      this.filters.kurs = value;
      this.showKursDropdown = false;
    },
    handleOutsideClick() {
      this.showKursDropdown = false;
      this.showConfigModal = false;
      this.showBackupDropdown = false;
    },
    toggleConfigModal() {
      this.showConfigModal = !this.showConfigModal;
      this.showBackupDropdown = false;
    },
    toggleBackupDropdown() {
      this.showBackupDropdown = !this.showBackupDropdown;
      this.showConfigModal = false;
    },
    async loadButtonsConfig() {
      try {
        const lsSync = localStorage.getItem("yuklama_btn_sync");
        const lsBackup = localStorage.getItem("yuklama_btn_backup");
        if (lsSync !== null) this.buttonsConfig.showSync = lsSync !== "false";
        if (lsBackup !== null) this.buttonsConfig.showBackup = lsBackup !== "false";
      } catch (e) {}

      try {
        const res = await axios.get(`${API}/backup/buttons-config`);
        if (res.data?.success && res.data?.buttons) {
          this.buttonsConfig.showSync = res.data.buttons.showSync !== false;
          this.buttonsConfig.showBackup = res.data.buttons.showBackup !== false;
        }
      } catch (e) {}
    },
    async saveButtonsConfig() {
      try {
        localStorage.setItem("yuklama_btn_sync", this.buttonsConfig.showSync);
        localStorage.setItem("yuklama_btn_backup", this.buttonsConfig.showBackup);
      } catch (e) {}

      try {
        await axios.post(`${API}/backup/buttons-config`, {
          showSync: this.buttonsConfig.showSync,
          showBackup: this.buttonsConfig.showBackup
        });
      } catch (e) {
        console.warn("Tugmalar sozlamasini serverga saqlashda ogohlantirish:", e);
      }
    },
    async triggerQuickBackup() {
      this.showBackupDropdown = false;
      this.backupLoading = true;
      try {
        const note = `Yuklama: ${this.kafedraName || "Kafedra"} (${this.filters.oquvYili || "2026-2027"})`;
        const res = await axios.post(`${API}/backup/create`, {
          note,
          type: "MANUAL"
        });
        if (res.data?.success) {
          alert(`✅ Baza nusxasi (Backup) muvaffaqiyatli saqlandi!\nNuqta hajmi: ${res.data.data?.sizeFormatted || ""}\nVaqt: ${new Date().toLocaleTimeString()}`);
        } else {
          alert("Nusxa olishda xatolik: " + (res.data?.error || "Noma'lum"));
        }
      } catch (err) {
        console.error("Quick backup error:", err);
        alert("Zaxira olishda xatolik: " + (err.response?.data?.error || err.message));
      } finally {
        this.backupLoading = false;
      }
    },
    goToBackupPage() {
      this.showBackupDropdown = false;
      this.$router.push("/backup");
    },
    changePage(page) {
      if (page < 1 || page > this.pageCount) return;
      this.currentPage = page;
      this.fetchData();
    },
    setSemester(value) {
      this.filters.semestrTuri = value;
      this.currentPage = 1;
      this.fetchData();
    },
    async reSync() {
      if (!this.filters.departmentId || !this.filters.oquvYili) {
        alert("Kafedra va o'quv yili tanlanmagan!");
        return;
      }
      this.syncing = true;
      try {
        await axios.post(`${API}/kafedra-yuklamalar/sync`, {
          departmentId: this.filters.departmentId,
          oquvYili: this.filters.oquvYili,
          semestrTuri: this.filters.semestrTuri || undefined
        });
        // Sync tugagandan keyin ma'lumotlarni qayta yuklash
        await this.fetchData();
      } catch (err) {
        console.error("Sync xatolik:", err);
        alert("Sync paytida xatolik yuz berdi!");
      } finally {
        this.syncing = false;
      }
    },
    applyYearFilter() {
      this.currentPage = 1;
      this.fetchData();
    },

    async addCustomWorkload() {
      if (!this.selectedCustomWorkload) return;
      
      const optionMap = {
        amaliyotInd: "Malakaviy amaliyot individual",
        amaliyotUni: "Malakaviy amaliyot universitet hududida",
        amaliyotTash: "Malakaviy amaliyot universitetdan tashqari hududda",
        bmiRahbar: "BMI rahbarlik",
        bmiDak: "BMI/DAK",
        magistr: "Magistrlik IPF va amaliyotga rahbarlik",
        mdIti: "MD - ITI ga rahbarlik qilish",
        mdDak: "MD/DAK",
        doktorant: "Doktorantga (stajyor tadqiqotchiga) rahbarlik",
        yadaYozmaGuruh: "YaDA yozma ish guruh soni",
        yadaYozmaTalaba: "YaDA yozma ish tekshirish",
        yadaTest: "YaDA test",
        ochiqLeksiya: "Ochiq leksiya",
        malakaOshirish: "Respublika ta'lim muassasalarida markazlarida malaka oshirishlarni o'tash"
      };

      const subjectName = optionMap[this.selectedCustomWorkload] || "Qo'shimcha yuklama";
      
      this.addingCustom = true;
      try {
        const payload = {
          departmentId: this.filters.departmentId,
          oquvYili: this.filters.oquvYili,
          semestrTuri: this.filters.semestrTuri || "hammasi",
          customField: this.selectedCustomWorkload,
          subjectName: subjectName
        };

        const res = await axios.post(`${API}/kafedra-yuklamalar/custom`, payload);
        if (res.data.success) {
          // Refresh the data to show the new row
          this.fetchData();
          this.selectedCustomWorkload = "";
        }
      } catch (err) {
        console.error("Qo'shimcha yuklama qo'shishda xatolik:", err);
        alert("Xatolik yuz berdi. Iltimos qaytadan urining.");
      } finally {
        this.addingCustom = false;
      }
    },

    getRowSpecialty(row) {
      if (!row) return "";
      if (row.isSyntheticGroup && row.guruhNomi) {
        const getClean = (s) => (s || "").replace(/\b(19|20)\d{2}\b/g, '').replace(/sirtqi|kunduzgi|kechki/gi, '').replace(/[^a-z0-9]/gi, '').toLowerCase();
        const gCleanCheck = getClean(row.guruhNomi);
        const singleRow = (this.rows || []).find(r => {
          if (!r.guruhNomi) return false;
          const parts = this.splitGroupNames(r.guruhNomi);
          return parts.length === 1 && (parts[0].trim() === row.guruhNomi.trim() || getClean(parts[0]) === gCleanCheck);
        });
        if (singleRow) {
          return singleRow.customSpecialty || singleRow.specialty?.name || "";
        }
      }
      if (!row.guruhNomi) return row.customSpecialty || row.specialty?.name || "";
      const groups = this.splitGroupNames(row.guruhNomi);
      if (groups.length <= 1) {
        return row.customSpecialty || row.specialty?.name || "";
      }
      const normalize = (str) => (str || "").replace(/\s+/g, ' ').trim().toLowerCase();
      const specialties = new Set();
      
      groups.forEach(gName => {
        const normKey = normalize(gName);
        const sp = this.guruhSpecialtyMap[gName] || this.guruhSpecialtyMap[normKey];
        if (sp) {
          sp.split(',').forEach(s => { if (s.trim()) specialties.add(s.trim()); });
        }
      });
      
      if (specialties.size > 0) return Array.from(specialties).join(', ');
      return row.customSpecialty || row.specialty?.name || "";
    },

    confirmDeleteRow(row) {
      if (!this.isEditable) {
        alert("Sizda ushbu amalni bajarish uchun ruxsat yo'q!");
        return;
      }
      this.deleteConfirmRow = row;
    },

    async doDeleteRow() {
      const row = this.deleteConfirmRow;
      if (!row) return;
      this.isDeleting = true;
      try {
        const res = await axios.delete(`${API}/kafedra-yuklamalar/${row._id}`);
        if (res.data.success) {
          this.deleteConfirmRow = null;
          this.fetchData();
        } else {
          alert(res.data.message || "O'chirishda xatolik yuz berdi");
        }
      } catch (err) {
        console.error("Qatorni o'chirishda xatolik:", err);
        alert("Xatolik yuz berdi. Iltimos qaytadan urining.");
      } finally {
        this.isDeleting = false;
      }
    },

    getLoad(row, code) {
      // Nom bo'yicha kalit so'zlar va alternativ kodlar (HEMIS versiyalari farq qiladi)
      const nameMap = {
        "11": ["ma'ruza", "maruza", "lecture"],
        "12": ["laboratoriya", "laboratory", "lab"],
        "13": ["amaliy", "amaliyot", "practical", "practice"],
        "14": ["seminar"],
      };
      const altCodes = { "11": ["1"], "12": ["4"], "13": ["2","3"], "14": ["3","5"] };
      const keywords = nameMap[code] || [];
      const alts = altCodes[code] || [];

      const matchEntry = (tCode, tName) => {
        tCode = String(tCode || "");
        tName = String(tName || "").toLowerCase();
        return tCode === code || alts.includes(tCode) || keywords.some(k => tName.includes(k));
      };

      // 1) total_acload — HEMIS ning asosiy maydoni (snake_case: training_type, acload)
      if (row.total_acload?.length) {
        const found = row.total_acload.find(d =>
          matchEntry(d.training_type?.code, d.training_type?.name)
        );
        if (found) return found.acload || found.total_hours || 0;
      }

      // 2) subjectDetails — eski format (camelCase: trainingType, academic_load)
      if (row.subjectDetails?.length) {
        const found = row.subjectDetails.find(d =>
          matchEntry(d.trainingType?.code || d.training_type?.code,
                     d.trainingType?.name || d.training_type?.name)
        );
        if (found) return found.academic_load || found.acload || 0;
      }

      return 0;
    },



    getExam(row, type) {
      if (!row.subjectExamTypes?.length) return 0;
      const keywords = {
        oraliq: ["oraliq", "12", "1"],
        yakuniy: ["yakuniy", "final", "13", "2"],
        nazorat: ["nazorat", "3"],
      };
      const keys = keywords[type] || [];
      const found = row.subjectExamTypes.find((e) =>
        keys.some(
          (k) =>
            String(e.examType?.name).toLowerCase().includes(k) ||
            String(e.examType?.code) === k,
        ),
      );
      return found?.max_ball || 0;
    },


    calc(row) {
      const isMultiGroup = row.guruhNomi && this.splitGroupNames(row.guruhNomi).length > 1;
      const isSecGroup = !isMultiGroup && (!!row.isSyntheticGroup || !!row._isSecondaryInStream);
      const hasGuruh = !!row.guruhNomi || row.isCustom;
      const s = (typeof row.talabalarSoni === 'number' && row.talabalarSoni > 0) ? row.talabalarSoni : (hasGuruh ? (row.talabalarSoni || 0) : 0);
      const gListCount = row.guruhNomi ? this.splitGroupNames(row.guruhNomi).length : 0;
      // gBase va g pastda qayta hisoblanadi (guruh bo'lmasa 1 ga default qilinadi)
      const h = row.haftaSoni || 0;
      const name = (this.getSubjectNameDisplay(row) + " " + this.getSubjectCodeDisplay(row)).toLowerCase();
      const rgName = String(row.ratingGrade?.name || "").toLowerCase();
      const hasKursLoyihaDetail = Array.isArray(row.subjectDetails) && row.subjectDetails.some(d => String(d.trainingType?.name).toLowerCase().includes("kurs loyiha") || String(d.trainingType?.code) === "15");
      const hasKursIshiDetail = Array.isArray(row.subjectDetails) && row.subjectDetails.some(d => String(d.trainingType?.name).toLowerCase().includes("kurs ish") || String(d.trainingType?.code) === "16");
      
      // Keywords for specific types of work
      const exactKursIshiHimoya = name.includes("kurs ish himoya") || name.includes("kurs ishi himoya");
      const exactKursIshi = (name.includes("kurs ish") || hasKursIshiDetail) && !exactKursIshiHimoya && !name.includes("kurs loyiha") && !hasKursLoyihaDetail;
      
      const exactKursLoyihasiHimoya = name.includes("kurs loyiha himoya") || name.includes("kurs loyihasi himoya");
      const exactKursLoyihasi = (name.includes("kurs loyiha") || name.includes("kurs-loyiha") || name.includes("kurs loyihas") || rgName.includes("kurs loyiha") || hasKursLoyihaDetail) && !exactKursLoyihasiHimoya;

      const hasAuditoryDetails = Array.isArray(row.subjectDetails) && row.subjectDetails.some(d => ["11", "13", "12", "14", "1", "2", "3", "4", "5"].includes(String(d.trainingType?.code || d.training_type?.code)));
      const isAmaliyot = !hasAuditoryDetails && (
        name.includes("malakaviy amaliyot") ||
        name.includes("o'quv amaliyot") ||
        name.includes("ishlab chiqarish amaliyot") ||
        name.includes("pedagogik amaliyot") ||
        name.includes("diplom oldi amaliyot") ||
        name.includes("praktika") ||
        (name.startsWith("amaliyot") && !name.includes("buxgalteriya"))
      );
      const isBMI = /\bbmi\b/.test(name) || name.includes("malakaviy ish");
      const isMagistr = !hasAuditoryDetails && !name.includes("avtomagistral") && name.includes("magistr");
      const isMD = name.includes("md - iti") || name.includes("md/dak");
      const isPhD = name.includes("doktorant") || name.includes("stajyor");
      const isYADA = /\byada\b/.test(name) || name.includes("yakuniy davlat");
      const isOchiqLeksiya = name.includes("ochiq leksiya");
      const isMalakaOshirish = !hasAuditoryDetails && (name.includes("malaka oshirishlarni o'tash") || name === "malaka oshirish" || name.startsWith("malaka oshirish") || (row.isCustom && name.includes("malaka oshirish")));
      // Ilmiy-pedagogik ish: oraliq va yakuniy nazorat hisoblanmaydi
      const isIlmiyPedagogik = name.includes("ilmiy-pedagogik") || name.includes("ilmiy pedagogik");

      const specName = (row.specialty?.name || "").toLowerCase();
      const isTech = specName.includes("texnik") || specName.includes("muhandis") || specName.includes("it ") || specName.includes("axborot") || specName.includes("dastur");
      const bmiMult = isTech ? 30 : 25;

      const isStajyor = name.includes("stajyor") || name.includes("tadqiqotchi");
      const phdMult = isStajyor ? 75 : 100;

      const isExtra = (row.isCustom && !row.isSyntheticGroup && !!row.customField) || 
                      exactKursIshi || exactKursIshiHimoya || 
                      exactKursLoyihasi || exactKursLoyihasiHimoya || 
                      isAmaliyot || isBMI || isMagistr || isMD || isPhD || isYADA || isOchiqLeksiya || isMalakaOshirish ||
                      name.includes("hisob-chizma");

      // Oraliq/yakuniy hisoblanmaydigan fanlar (audit soatlari normal ko'rsatiladi)
      const noExam = isExtra || isIlmiyPedagogik;

      // Guruh yo'q bo'lsa ham HEMIS soatlarini ko'rsatish uchun multiplier 1 ga teng bo'ladi
      // hasGuruh - faqat talabalar soni (s) uchun ishlatiladi
      const o = row.oqimSoni || row.oqim || 1;  // Guruh bo'lmasa 1 oqim
      const gBase = hasGuruh ? (row.guruhSoni || gListCount || 1) : 1;  // Guruh bo'lmasa 1 guruh
      const g = hasGuruh ? gBase : 0;  // Tashqari hisob-kitoblar uchun (amaliyotUni, yadaYozma...)
      const kgBase = Math.max(row.kichikGuruhSoni || 1, gBase);  // Kichik guruh
      const labRaw = this.getLoad(row, "12");

      const amaliyLoad = this.getLoad(row, "13");
      const seminarLoad = this.getLoad(row, "14");
      const audit = {
        maruza:       (isExtra || isSecGroup) ? 0 : (typeof row.maruza       === 'number' && row.maruza       > 0 ? row.maruza       : Math.round(this.getLoad(row, "11") * o)),
        amaliy:       (isExtra || isSecGroup) ? 0 : Math.round((typeof row.amaliy       === 'number' && row.amaliy       > 0 ? row.amaliy       : amaliyLoad)  * gBase),
        seminar:      (isExtra || isSecGroup) ? 0 : Math.round((typeof row.seminar      === 'number' && row.seminar      > 0 ? row.seminar      : seminarLoad) * gBase),
        laboratoriya: (isExtra || isSecGroup) ? 0 : Math.round((typeof row.laboratoriya === 'number' && row.laboratoriya > 0 ? row.laboratoriya : labRaw)       * kgBase)
      };


      const res = {
        audit,
        jamiAudit: Math.round(audit.maruza + audit.amaliy + audit.seminar + audit.laboratoriya),
        kursIshi: exactKursIshi ? Math.round(s * 2) : 0,
        kursIshiHimoya: (exactKursIshi || exactKursIshiHimoya) ? Math.round(s * 0.4) : 0,
        kursLoyihasi: exactKursLoyihasi ? Math.round(s * 3) : 0,
        kursLoyihasiHimoya: (exactKursLoyihasi || exactKursLoyihasiHimoya) ? Math.round(s * 0.6) : 0,
        hisobChizma: name.includes("hisob-chizma") ? Math.round(s * 0.3) : 0,
        oraliq:  (noExam || isSecGroup) ? 0 : (s > 0 ? Math.round(s * 0.2) : 0),
        yakuniy: (noExam || isSecGroup) ? 0 : (s > 0 ? Math.round(s * 0.3) : 0),
        amaliyotInd: (!row.isCustom && isAmaliyot) || (row.isCustom && name === "malakaviy amaliyot individual") ? Math.round(s * 2) : 0,
        amaliyotUni: (!row.isCustom && isAmaliyot) || (row.isCustom && name === "malakaviy amaliyot universitet hududida") ? Math.round(3 * 6 * h * g) : 0,
        amaliyotTash: (!row.isCustom && isAmaliyot) || (row.isCustom && name === "malakaviy amaliyot universitetdan tashqari hududda") ? Math.round(6 * 6 * h * g) : 0,
        bmiRahbar: (!row.isCustom && isBMI) || (row.isCustom && name === "bmi rahbarlik") ? Math.round(s * bmiMult) : 0,
        bmiDak: (!row.isCustom && isBMI) || (row.isCustom && name === "bmi/dak") ? Math.round(s * 0.4) : 0,
        magistr: (!row.isCustom && isMagistr) || (row.isCustom && name === "magistrlik ipf va amaliyotga rahbarlik") ? Math.round(s * 50) : 0,
        mdIti: (!row.isCustom && isMD) || (row.isCustom && name === "md - iti ga rahbarlik qilish") ? Math.round(s * 50) : 0,
        mdDak: (!row.isCustom && isMD) || (row.isCustom && name === "md/dak") ? Math.round(s * 0.8) : 0,
        doktorant: (!row.isCustom && isPhD) || (row.isCustom && name === "doktorantga (stajyor tadqiqotchiga) rahbarlik") ? Math.round(s * phdMult) : 0,
        yadaYozmaGuruh: (!row.isCustom && isYADA) || (row.isCustom && name === "yada yozma ish guruh soni") ? Math.round(g * 3) : 0,
        yadaYozmaTalaba: (!row.isCustom && isYADA) || (row.isCustom && name === "yada yozma ish tekshirish") ? Math.round(s * 0.2) : 0,
        yadaTest: (!row.isCustom && isYADA) || (row.isCustom && name === "yada test") ? Math.round(g * 1) : 0,
        ochiqLeksiya: (!row.isCustom && isOchiqLeksiya) || (row.isCustom && name === "ochiq leksiya") ? Math.round(g * 5) : 0,
        malakaOshirish: (!row.isCustom && isMalakaOshirish) || (row.isCustom && (name === "respublika ta'lim muassasalarida markazlarida malaka oshirishlarni o'tash" || name.includes("malaka oshirish"))) ? (typeof row.malakaOshirish === 'number' ? row.malakaOshirish : 0) : 0
      };
      const tashqariKeys = [
        "kursIshi", "kursIshiHimoya", "kursLoyihasi", "kursLoyihasiHimoya", 
        "hisobChizma", "oraliq", "yakuniy", "amaliyotInd", "amaliyotUni", 
        "amaliyotTash", "bmiRahbar", "bmiDak", "magistr", "mdIti", "mdDak", 
        "doktorant", "yadaYozmaGuruh", "yadaYozmaTalaba", "yadaTest", "ochiqLeksiya",
        "malakaOshirish"
      ];

      // Apply manual overrides from saved row data (but NOT for secondary groups)
      if (!isSecGroup) {
        tashqariKeys.forEach(f => {
          if (typeof row[f] === 'number') {
            res[f] = row[f];
          }
        });
      }

      res.jamiTashqari = Math.round(tashqariKeys.reduce((acc, key) => acc + (res[key] || 0), 0));
      res.jami = Math.round(res.jamiAudit + res.jamiTashqari);
      
      return res;
    },

    openEditModal(row, field, title, inputType = 'number') {
      if (!this.isEditable) return;
      this.editingRow = row;
      this.editingField = field;
      this.modalTitle = title;
      if (field === 'guruhSoni') {
        const gListCount = row.guruhNomi ? row.guruhNomi.split(',').map(s => s.trim()).filter(Boolean).length : 0;
        this.editValue = row.guruhSoni || gListCount || 1;
      } else {
        this.editValue = row[field] || "";
      }
      this.inputType = inputType;
      this.isTashqariEdit = false;
      this.showModal = true;
    },

    openTashqariModal(row, field, title) {
      if (!this.isEditable) return;
      this.editingRow = row;
      this.editingField = field;
      this.modalTitle = title;
      this.isTashqariEdit = true;

      const configs = {
        kursIshi: { label: "Talabalar soni", multiplier: 2, formulaText: "Talaba soni * 2" },
        kursIshiHimoya: { label: "Talabalar soni", multiplier: 0.4, formulaText: "Talaba soni * 0.4" },
        kursLoyihasi: { label: "Talabalar soni", multiplier: 3, formulaText: "Talaba soni * 3" },
        kursLoyihasiHimoya: { label: "Talabalar soni", multiplier: 0.6, formulaText: "Talaba soni * 0.6" },
        hisobChizma: { label: "Talabalar soni", multiplier: 0.3, formulaText: "Talaba soni * 0.3" },
        oraliq: { label: "Talabalar soni", multiplier: 0.2, formulaText: "Talaba soni * 0.2" },
        yakuniy: { label: "Talabalar soni", multiplier: 0.3, formulaText: "Talaba soni * 0.3" },
        amaliyotInd: { label: "Talabalar soni", multiplier: 2, formulaText: "Talaba soni * 2" },
        amaliyotUni: { type: "multi_vars", formulaText: "3 * 6 * Hafta soni * Guruh soni", multiplier: 18 },
        amaliyotTash: { type: "multi_vars", formulaText: "6 * 6 * Hafta soni * Guruh soni", multiplier: 36 },
        bmiRahbar: {
          type: "selection",
          options: [
            { text: "Boshqa ta'lim yo'nalishlari (*25)", value: 25 },
            { text: "Texnik yo'nalishlar (*30)", value: 30 }
          ],
          label: "Talabalar soni"
        },
        bmiDak: { label: "Talabalar soni", multiplier: 0.4, formulaText: "Talaba soni * 0.4" },
        magistr: { label: "Talabalar soni", multiplier: 50, formulaText: "Talaba soni * 50" },
        mdIti: { label: "Talabalar soni", multiplier: 50, formulaText: "Talaba soni * 50" },
        mdDak: { label: "Talabalar soni", multiplier: 0.8, formulaText: "Talaba soni * 0.8" },
        doktorant: {
          type: "selection",
          options: [
            { text: "Stajyor-tadqiqotchi (*75)", value: 75 },
            { text: "Doktorant (*100)", value: 100 }
          ],
          label: "Talabalar soni"
        },
        yadaYozmaGuruh: { label: "Guruhlar soni", multiplier: 3, formulaText: "Guruh soni * 3" },
        yadaYozmaTalaba: { label: "Talabalar soni", multiplier: 0.2, formulaText: "Talaba soni * 0.2" },
        yadaTest: { label: "Guruhlar soni", multiplier: 1, formulaText: "Guruh soni * 1" },
        ochiqLeksiya: { label: "Darslar soni", multiplier: 5, formulaText: "Dars soni * 5" },
        malakaOshirish: { label: "Soat", multiplier: 1, formulaText: "Soat * 1" }
      };

      this.tashqariConfig = configs[field] || { label: "Qiymat", multiplier: 1, formulaText: "" };

      // Get current displayed value (manual override or calculated)
      const currentVal = this.calc(row)[field] || 0;

      // Initialize inputs based on the configuration type
      if (this.tashqariConfig.type === 'multi_vars') {
        this.tashqariHafta = row.haftaSoni || 0;
        this.tashqariGuruh = row.guruhSoni || 0;
      } else if (this.tashqariConfig.type === 'selection') {
        if (field === 'bmiRahbar') {
          const specName = (row.specialty?.name || "").toLowerCase();
          const isTech = specName.includes("texnik") || specName.includes("muhandis") || specName.includes("it ") || specName.includes("axborot") || specName.includes("dastur");
          this.tashqariSelectVal = (isTech || (currentVal > 0 && currentVal % 30 === 0)) ? 30 : 25;
        } else if (field === 'doktorant') {
          this.tashqariSelectVal = (currentVal > 0 && currentVal % 75 === 0) ? 75 : 100;
        } else {
          this.tashqariSelectVal = this.tashqariConfig.options[0].value;
        }
        
        if (currentVal > 0) {
          this.tashqariInputVal = Number((currentVal / this.tashqariSelectVal).toFixed(1));
        } else {
          this.tashqariInputVal = row.talabalarSoni || 0;
        }
      } else {
        const mult = this.tashqariConfig.multiplier || 1;
        if (currentVal > 0) {
          this.tashqariInputVal = Number((currentVal / mult).toFixed(1));
        } else {
          if (['yadaYozmaGuruh', 'yadaTest'].includes(field)) {
            this.tashqariInputVal = row.guruhSoni || 0;
          } else if (field === 'ochiqLeksiya' || field === 'malakaOshirish') {
            this.tashqariInputVal = 0;
          } else {
            this.tashqariInputVal = row.talabalarSoni || 0;
          }
        }
      }

      this.recalcTashqari();
      this.showModal = true;
    },

    getActiveFormulaText() {
      if (!this.tashqariConfig) return "";
      if (this.tashqariConfig.type === 'selection') {
        const selectedOpt = this.tashqariSelectVal;
        return `${this.tashqariConfig.label || 'Talaba soni'} * ${selectedOpt || 'multiplier'}`;
      }
      return this.tashqariConfig.formulaText || "";
    },

    getTashqariExpression() {
      if (!this.tashqariConfig) return "";
      if (this.tashqariConfig.type === 'multi_vars') {
        const coef = this.editingField === 'amaliyotUni' ? '3 * 6' : '6 * 6';
        return `${coef} * ${this.tashqariHafta || 0} * ${this.tashqariGuruh || 0}`;
      }
      if (this.tashqariConfig.type === 'selection') {
        return `${this.tashqariInputVal || 0} * ${this.tashqariSelectVal || 0}`;
      }
      return `${this.tashqariInputVal || 0} * ${this.tashqariConfig.multiplier || 0}`;
    },

    recalcTashqari() {
      if (!this.tashqariConfig) return;
      let res = 0;
      if (this.tashqariConfig.type === 'multi_vars') {
        res = (this.tashqariConfig.multiplier || 1) * (Number(this.tashqariHafta) || 0) * (Number(this.tashqariGuruh) || 0);
      } else if (this.tashqariConfig.type === 'selection') {
        res = (Number(this.tashqariInputVal) || 0) * (Number(this.tashqariSelectVal) || 0);
      } else {
        res = (Number(this.tashqariInputVal) || 0) * (this.tashqariConfig.multiplier || 0);
      }
      this.editValue = Math.round(res);
    },

    closeModal() {
      this.showModal = false;
      this.editingRow = null;
      this.editingField = "";
      this.editValue = "";
      this.isTashqariEdit = false;
    },

    async clearValue() {
      if (this.isTashqariEdit) {
        // Remove manual override, revert to formula
        this.editValue = null;
        await this.confirmValue();
        return;
      }
      this.editValue = "";
    },

    async confirmValue() {
      if (!this.editingRow) return;

      const isSynth = !this.editingRow._id || String(this.editingRow._id).startsWith('synth_');

      if (isSynth) {
        let valueToSave = this.editValue;
        if (this.isTashqariEdit && valueToSave === null) {
          delete this.editingRow[this.editingField];
        } else {
          if (this.inputType === 'number' || ['talabalarSoni', 'oqimSoni', 'guruhSoni', 'kichikGuruhSoni'].includes(this.editingField)) {
            try {
              if (typeof valueToSave === 'string' && /^[0-9+\-*/. ]+$/.test(valueToSave)) {
                valueToSave = eval(valueToSave);
              } else {
                valueToSave = Number(valueToSave);
              }
            } catch (e) {
              valueToSave = Number(valueToSave);
            }
            if (isNaN(valueToSave)) valueToSave = 0;
          }
          this.editingRow[this.editingField] = valueToSave;
          if (this.editingField === 'customSubject') {
            if (!this.editingRow.subject || typeof this.editingRow.subject !== 'object') {
              this.editingRow.subject = { id: null, name: valueToSave, code: "CUSTOM" };
            } else {
              this.editingRow.subject.name = valueToSave;
            }
          }
        }

        try {
          const createData = { ...this.editingRow };
          delete createData._id;
          delete createData.id;
          delete createData.createdAt;
          delete createData.updatedAt;
          delete createData.__v;
          createData.isSyntheticGroup = true;

          const res = await axios.post(`${API}/kafedra-yuklamalar`, createData);
          if (res.data && res.data.data) {
            const savedDoc = res.data.data;
            Object.assign(this.editingRow, savedDoc);
            this.rows.push(savedDoc);
          }
        } catch (err) {
          console.warn("Could not persist synthetic row:", err);
        }

        this.closeModal();
        return;
      }

      try {
        const payload = {};
        if (this.isTashqariEdit && this.editValue === null) {
          // Remove override - send null to backend
          payload[this.editingField] = null;
          await axios.patch(`${API}/kafedra-yuklamalar/${this.editingRow._id}`, payload);
          delete this.editingRow[this.editingField];
        } else {
          let valueToSave = this.editValue;
          if (this.inputType === 'number' || ['talabalarSoni', 'oqimSoni', 'guruhSoni', 'kichikGuruhSoni'].includes(this.editingField)) {
            try {
              if (typeof valueToSave === 'string' && /^[0-9+\-*/. ]+$/.test(valueToSave)) {
                valueToSave = eval(valueToSave);
              } else {
                valueToSave = Number(valueToSave);
              }
            } catch (e) {
              valueToSave = Number(valueToSave);
            }
            if (isNaN(valueToSave)) valueToSave = 0;
          }
          
          payload[this.editingField] = valueToSave;
          if (this.editingField === 'customSubject') {
            payload.subject = { id: this.editingRow.subject?.id || null, name: valueToSave, code: "CUSTOM" };
          }
          await axios.patch(`${API}/kafedra-yuklamalar/${this.editingRow._id}`, payload);
          this.editingRow[this.editingField] = valueToSave;
          if (this.editingField === 'customSubject') {
            if (!this.editingRow.subject || typeof this.editingRow.subject !== 'object') {
              this.editingRow.subject = { id: null, name: valueToSave, code: "CUSTOM" };
            } else {
              this.editingRow.subject.name = valueToSave;
            }
          }
        }
        
        this.closeModal();
      } catch (err) {
        console.error("Xatolik", err);
        alert("Saqlashda xatolik yuz berdi.");
      }
    },

    setTrainingType(type) {
      this.activeTrainingType = type || 'maruza';
      this.groupAddMode = 'zero';
    },

    openSubjectModal(row, activeType = 'maruza') {
      if (!this.isEditable) return;
      this.subjectModalRow = row;
      this.activeTrainingType = activeType || 'maruza';
      this.groupAddMode = (activeType === 'maruza') ? 'maruza0' : 'normal';
      
      const displayedGuruh = this.getFullGuruhNomi(row);
      this.subjectModalGuruh = displayedGuruh !== "—" ? displayedGuruh : (row.guruhNomi || "");
      
      this.subjectModalSpecialty = row.customSpecialty || row.specialty?.name || "";
      this.groupSpecialtyMap = {};
      this.removedGroupsFromSubject = [];
      
      this.resolveAllGroupCounts();

      // Prefer existing talabalarSoni — only recalculate if it's missing/zero
      const existingTalaba = Number(row.talabalarSoni);
      if (existingTalaba > 0) {
        this.subjectModalTalaba = existingTalaba.toString();
      } else {
        // No existing value — calculate from individual group counts
        const modalGuruhs = row.guruhNomi ? this.splitGroupNames(row.guruhNomi) : [];
        let calculatedTalaba = 0;
        modalGuruhs.forEach(gName => {
          calculatedTalaba += this.getGroupTalabaCount(gName);
        });
        this.subjectModalTalaba = calculatedTalaba > 0 ? calculatedTalaba.toString() : "";
      }

      this.subjectModalHours = {
        maruza: typeof row.maruza === 'number' ? row.maruza : null,
        amaliy: typeof row.amaliy === 'number' ? row.amaliy : null,
        seminar: typeof row.seminar === 'number' ? row.seminar : null,
        laboratoriya: typeof row.laboratoriya === 'number' ? row.laboratoriya : null
      };
      // Snapshot — foydalanuvchi nimani o'zgartirganini aniqlash uchun
      this.originalModalHours = { ...this.subjectModalHours };

      this.showSubjectModal = true;

      this.$nextTick(() => {
        this.recalcSpecialties();
      });
    },

    closeSubjectModal() {
      this.showSubjectModal = false;
      this.subjectModalRow = null;
      this.subjectModalGuruh = "";
      this.subjectModalTalaba = "";
      this.subjectModalSpecialty = "";
      this.subjectModalSelectedGroup = "";
      this.activeTrainingType = "maruza";
      this.groupAddMode = "maruza0";
      this.groupAddStrategy = "separate";
      this.selectedGroupForSeparate = null;
      this.addedGroupsForMaruza0 = [];
      this.subjectModalHours = { maruza: null, amaliy: null, seminar: null, laboratoriya: null };
      this.groupSpecialtyMap = {};
    },

    resetModalHours() {
      this.subjectModalHours = {
        maruza: null,
        amaliy: null,
        seminar: null,
        laboratoriya: null
      };
    },

    recalcSpecialties() {
      const specialties = new Set();
      const currentGuruhs = this.parsedGuruhs;

      for (const gName of currentGuruhs) {
        let sp = this.groupSpecialtyMap[gName] || this.guruhSpecialtyMap[gName];
        if (!sp) {
          const matchingRow = this.rows.find(r =>
            (r.guruhNomi && r.guruhNomi.split(',').map(s => s.trim()).includes(gName)) ||
            (r.curriculumName && r.curriculumName.trim() === gName.trim()) ||
            (this.getFullGuruhNomi(r) === gName.trim())
          );
          if (matchingRow) {
            sp = matchingRow.specialty?.name || matchingRow.customSpecialty;
          }
        }
        if (!sp) {
          const info = (this.availableGroups || []).find(g => 
            g.rawName === gName || g.shortName === gName || g.curriculumName === gName || g.id === gName
          );
          if (info && info.specialtyName) {
            sp = info.specialtyName;
          }
        }

        if (sp) {
          sp.split(',').forEach(s => { if (s.trim()) specialties.add(s.trim()); });
        }
      }

      if (specialties.size === 0 && this.subjectModalRow?.specialty?.name) {
        specialties.add(this.subjectModalRow.specialty.name.trim());
      }
      
      this.subjectModalSpecialty = Array.from(specialties).join(', ');
    },

    getGroupTalabaCount(gName) {
      if (!gName) return 0;
      const cleanG = gName.trim();
      const getClean = (s) => (s || "").replace(/\b(19|20)\d{2}\b/g, '').replace(/sirtqi|kunduzgi|kechki/gi, '').replace(/[^a-z0-9]/gi, '').toLowerCase();
      const cleanKey = getClean(cleanG);
      const getNorm = (s) => (s || "").replace(/[^a-z0-9]/gi, '').toLowerCase();
      const normKey = getNorm(cleanG);
      const getCode = (s) => {
        const m = (s || "").match(/([a-zA-Z]{2,6}\s*\d{1,4}[a-zA-Z]?|\d+-\d+)/);
        return m ? m[0].replace(/\s+/g, '').toUpperCase() : "";
      };
      const codeKey = getCode(cleanG);

      if (this.knownGroupCounts) {
        if (this.knownGroupCounts[cleanG] > 0) return Number(this.knownGroupCounts[cleanG]);
        if (this.knownGroupCounts[cleanKey] > 0) return Number(this.knownGroupCounts[cleanKey]);
        if (this.knownGroupCounts[normKey] > 0) return Number(this.knownGroupCounts[normKey]);
        if (codeKey && this.knownGroupCounts[codeKey] > 0) return Number(this.knownGroupCounts[codeKey]);
      }

      const info = (this.availableGroups || []).find(g => {
        if (g.rawName === cleanG || g.id === cleanG || g.shortName === cleanG || g.curriculumName === cleanG) return true;
        if (getNorm(g.rawName) === normKey || getNorm(g.shortName) === normKey || getNorm(g.curriculumName) === normKey) return true;
        if (codeKey && (getCode(g.rawName) === codeKey || getCode(g.shortName) === codeKey || getCode(g.curriculumName) === codeKey)) return true;
        return false;
      });
      if (info && info.talaba && Number(info.talaba) > 0) {
        return Number(info.talaba);
      }

      const singleGroupRow = this.rows.find(r => {
        if (!r.guruhNomi && !r.curriculumName) return false;
        const fullN = this.getFullGuruhNomi(r);
        if (r.curriculumName === cleanG || getNorm(r.curriculumName) === normKey) return true;
        if (fullN === cleanG || getNorm(fullN) === normKey) return true;
        if (codeKey && (getCode(r.curriculumName) === codeKey || getCode(fullN) === codeKey)) return true;

        if (r.guruhNomi) {
          const parts = r.guruhNomi.split(',').map(s => s.trim()).filter(Boolean);
          if (parts.length === 1) {
            if (parts[0] === cleanG || getClean(parts[0]) === cleanKey || getNorm(parts[0]) === normKey) return true;
            if (codeKey && getCode(parts[0]) === codeKey) return true;
          }
        }
        return false;
      });
      if (singleGroupRow && singleGroupRow.talabalarSoni) {
        const val = Number(singleGroupRow.talabalarSoni);
        if (val > 0) {
          this.knownGroupCounts[cleanG] = val;
          return val;
        }
      }

      const matchingRow = this.rows.find(r => {
        if (!r.guruhNomi) return false;
        const rGroups = r.guruhNomi.split(',').map(s => s.trim()).filter(Boolean);
        return rGroups.some(g => g === cleanG || getClean(g) === cleanKey || getNorm(g) === normKey || (codeKey && getCode(g) === codeKey));
      });
      if (matchingRow && matchingRow.talabalarSoni) {
        const rGroups = matchingRow.guruhNomi.split(',').map(s => s.trim()).filter(Boolean);
        if (rGroups.length === 1) {
          return Number(matchingRow.talabalarSoni);
        } else {
          return Math.round(Number(matchingRow.talabalarSoni) / rGroups.length);
        }
      }
      return 0;
    },

    handleGroupAdd(val) {
      if (!val) return;
      const group = this.availableGroups.find(g => g.id === val);
      this.subjectModalSelectedGroup = "";
      if (!group) return;

      if (group.talaba && Number(group.talaba) > 0) {
        this.knownGroupCounts[group.rawName] = Number(group.talaba);
      }

      this.$nextTick(() => {
        const currentGuruhs = this.parsedGuruhs;
        if (!currentGuruhs.includes(group.rawName)) {
          if (this.subjectModalGuruh) {
            this.subjectModalGuruh += ", " + group.rawName;
          } else {
            this.subjectModalGuruh = group.rawName;
          }

          if (group.talaba && Number(group.talaba) > 0) {
            this.subjectModalTalaba = (Number(this.subjectModalTalaba || 0) + Number(group.talaba)).toString();
          } else {
            const addedCount = this.getGroupTalabaCount(group.rawName);
            this.subjectModalTalaba = (Number(this.subjectModalTalaba || 0) + addedCount).toString();
          }
        }

        if (!this.addedGroupsForMaruza0.some(g => g.rawName === group.rawName)) {
          this.addedGroupsForMaruza0.push(group);
        }

        if (group.specialtyName) {
          this.groupSpecialtyMap[group.rawName] = group.specialtyName;
        }
        
        this.resetModalHours();
        this.recalcSpecialties();
      });
    },

    removeGuruh(gName) {
      let guruhs = this.parsedGuruhs;
      const index = guruhs.indexOf(gName);
      if (index > -1) {
        guruhs.splice(index, 1);
        this.subjectModalGuruh = guruhs.join(', ');
      }
      
      if (!this.removedGroupsFromSubject.includes(gName)) {
        this.removedGroupsFromSubject.push(gName);
      }

      this.addedGroupsForMaruza0 = this.addedGroupsForMaruza0.filter(g => g.rawName !== gName);

      if (this.groupSpecialtyMap[gName]) {
        delete this.groupSpecialtyMap[gName];
      }

      this.resetModalHours();

      this.recalcSpecialties();
    },

    async confirmSubjectModal() {
      if (this.isSavingModal) return;
      this.isSavingModal = true;
      const row = this.subjectModalRow;
      if (!row) {
        this.isSavingModal = false;
        return;
      }
      let talabaVal = this.subjectModalTalaba;
      if (typeof talabaVal === 'string') {
        talabaVal = talabaVal.trim();
        if (/^[0-9+\-*/. ]+$/.test(talabaVal)) {
          try {
            talabaVal = eval(talabaVal);
          } catch (e) {
            talabaVal = parseFloat(talabaVal) || 0;
          }
        } else {
          talabaVal = parseFloat(talabaVal) || 0;
        }
      } else {
        talabaVal = Number(talabaVal) || 0;
      }
      if (!talabaVal && row.talabalarSoni) {
        talabaVal = row.talabalarSoni;
      }

      // Auto-calculate guruhSoni from the number of groups in guruhNomi
      const guruhList = (this.subjectModalGuruh || '').split(',').map(s => s.trim()).filter(Boolean);
      const autoGuruhSoni = guruhList.length > 1 ? guruhList.length : (row.guruhSoni || 1);

      const payload = {
        guruhNomi: this.subjectModalGuruh,
        talabalarSoni: talabaVal,
        customSpecialty: this.subjectModalSpecialty,
        guruhSoni: autoGuruhSoni,
      };

      ['maruza', 'amaliy', 'seminar', 'laboratoriya'].forEach(key => {
        const inputVal = this.subjectModalHours[key];
        const originalVal = (this.originalModalHours || {})[key];
        // Faqat foydalanuvchi o'zgartirgan yoki tozalagan maydonlarni yuborish
        if (inputVal === originalVal) return; // O'zgartirilmagan — yuklamaslik
        if (inputVal !== null && inputVal !== '' && !isNaN(Number(inputVal))) {
          payload[key] = Number(inputVal); // Yangi qiymat
        } else if (originalVal !== null) {
          payload[key] = null; // Eski override tozalandi
        }
        // else: ikkalasi ham null edi — yubormaslik
      });

      try {
        const rowId = row._id || row.itemId || row.id;
        if (rowId) {
          await axios.patch(`${API}/kafedra-yuklamalar/${rowId}`, payload);
        }
        
        row.guruhNomi = payload.guruhNomi;
        row.talabalarSoni = payload.talabalarSoni;
        row.customSpecialty = payload.customSpecialty;
        row.guruhSoni = payload.guruhSoni;

        ['maruza', 'amaliy', 'seminar', 'laboratoriya'].forEach(key => {
          if (!(key in payload)) return; // O'zgartirilmagan — skip
          if (payload[key] !== null && typeof payload[key] === 'number') {
            row[key] = payload[key];
            if (this.subjectModalRow) this.subjectModalRow[key] = payload[key];
          } else {
            delete row[key];
            if (this.subjectModalRow) delete this.subjectModalRow[key];
          }
        });

        const targetInRows = this.rows.find(r => (r._id && r._id === rowId) || (r.id && r.id === rowId));
        if (targetInRows) {
          targetInRows.guruhNomi = payload.guruhNomi;
          targetInRows.talabalarSoni = payload.talabalarSoni;
          targetInRows.customSpecialty = payload.customSpecialty;
          targetInRows.guruhSoni = payload.guruhSoni;
          ['maruza', 'amaliy', 'seminar', 'laboratoriya'].forEach(key => {
            if (!(key in payload)) return; // O'zgartirilmagan — skip
            if (payload[key] !== null && typeof payload[key] === 'number') {
              targetInRows[key] = payload[key];
            } else {
              delete targetInRows[key];
            }
          });
        }

        const getClean = (s) => (s || "").replace(/\b(19|20)\d{2}\b/g, '').replace(/sirtqi|kunduzgi|kechki/gi, '').replace(/[^a-z0-9]/gi, '').toLowerCase();
        const getSubjectBase = (r) => {
          if (!r) return "";
          let name = "";
          if (r.subject) {
            if (typeof r.subject === 'object') {
              const sObj = Array.isArray(r.subject) ? r.subject[0] : r.subject;
              name = sObj?.name || sObj?.title || sObj?.subjectName || "";
            } else if (typeof r.subject === 'string' && !/^[0-9a-fA-F]{24}$/.test(r.subject)) {
              name = r.subject;
            }
          }
          if (!name && r.subjectDetails) {
            const sdObj = Array.isArray(r.subjectDetails) ? r.subjectDetails[0] : r.subjectDetails;
            name = sdObj?.name || sdObj?.title || sdObj?.subjectName || "";
          }
          if (!name && r.subjectName) name = r.subjectName;
          if (!name && r.customSubject) name = r.customSubject;
          if (!name && r.name) name = r.name;
          if (!name && r.fanNomi) name = r.fanNomi;

          return (name || "")
            .replace(/\u0430/g,'a').replace(/\u0410/g,'A')
            .replace(/\u0435/g,'e').replace(/\u0415/g,'E')
            .replace(/\u043E/g,'o').replace(/\u041E/g,'O')
            .replace(/\u0441/g,'c').replace(/\u0421/g,'C')
            .replace(/\u0440/g,'r').replace(/\u0420/g,'R')
            .replace(/\u0445/g,'x').replace(/\u0425/g,'X')
            .replace(/\|.*/g, '')
            .replace(/\([A-Z0-9\s-]+\)/gi, '')
            .replace(/\b[A-Z]{2,6}\d{2,6}\b/gi, '')
            .replace(/[^a-z]/gi, '')
            .toLowerCase();
        };

        const sameSubject = (r1, r2) => {
          if (!r1 || !r2) return false;
          const sBase1 = getSubjectBase(r1);
          const sBase2 = getSubjectBase(r2);
          if (sBase1 && sBase2 && (sBase1 === sBase2 || sBase1.includes(sBase2) || sBase2.includes(sBase1))) return true;

          const id1 = typeof r1.subject === 'object' ? (r1.subject?._id || r1.subject?.id) : (typeof r1.subject === 'string' ? r1.subject : "");
          const id2 = typeof r2.subject === 'object' ? (r2.subject?._id || r2.subject?.id) : (typeof r2.subject === 'string' ? r2.subject : "");
          if (id1 && id2 && String(id1) === String(id2)) return true;

          const code1 = typeof r1.subject === 'object' ? (r1.subject?.code || "") : (r1.subjectCode || "");
          const code2 = typeof r2.subject === 'object' ? (r2.subject?.code || "") : (r2.subjectCode || "");
          if (code1 && code2 && String(code1).toLowerCase().trim() === String(code2).toLowerCase().trim()) return true;
          
          let name1 = typeof r1.subject === 'object' ? (r1.subject?.name || r1.subject?.title || "") : "";
          if (!name1 && r1.subjectDetails) name1 = r1.subjectDetails[0]?.name || r1.subjectDetails?.name || "";
          if (!name1) name1 = r1.fanNomi || "";
          
          let name2 = typeof r2.subject === 'object' ? (r2.subject?.name || r2.subject?.title || "") : "";
          if (!name2 && r2.subjectDetails) name2 = r2.subjectDetails[0]?.name || r2.subjectDetails?.name || "";
          if (!name2) name2 = r2.fanNomi || "";
          
          if (name1 && name2) {
            const n1 = name1.trim().toLowerCase();
            const n2 = name2.trim().toLowerCase();
            if (n1 === n2) return true;
            
            // Fuzzy match for minor spelling differences (e.g. muxofazasi vs muhofazasi)
            const c1 = n1.replace(/[^a-zа-я0-9]/gi, '').replace(/x/g, 'h');
            const c2 = n2.replace(/[^a-zа-я0-9]/gi, '').replace(/x/g, 'h');
            if (c1 && c2 && (c1 === c2 || c1.includes(c2) || c2.includes(c1))) return true;
          }

          return false;
        };

        // Get all secondary groups (index >= 1) from the updated modal guruhNomi
        const currentParsed = (this.subjectModalGuruh || "").split(',').map(s => s.trim()).filter(Boolean);
        const secondaryGroupNames = currentParsed.slice(1);

        const groupsToProcess = [...this.addedGroupsForMaruza0];
        secondaryGroupNames.forEach(gName => {
          if (!groupsToProcess.some(g => getClean(g.rawName) === getClean(gName))) {
            groupsToProcess.push({ rawName: gName });
          }
        });

        if (this.selectedGroupForSeparate && !groupsToProcess.some(g => getClean(g.rawName) === getClean(this.selectedGroupForSeparate.rawName))) {
          groupsToProcess.push(this.selectedGroupForSeparate);
        }

        if (groupsToProcess.length > 0) {
          for (const sepGroup of groupsToProcess) {
            const sepRaw = (sepGroup.rawName || "").trim();
            const sepClean = getClean(sepRaw);
            const sepNorm = sepRaw.replace(/[^a-z0-9]/gi, '').toLowerCase();
            const sepCode = ((sepRaw.match(/([a-zA-Z]{2,6}\s*\d{1,4}[a-zA-Z]?|\d+-\d+)/) || [])[0] || "").replace(/\s+/g, '').toUpperCase();

            let existingRow = this.rows.find(r => {
              if (r === row || (rowId && (r._id === rowId || r.id === rowId))) return false;
              if (!r.guruhNomi && !r.curriculumName) return false;
              const gList = (r.guruhNomi || "").split(',').map(s => s.trim()).filter(Boolean);
              if (gList.length > 1) return false;

              const rGuruh = (r.guruhNomi || "").trim();
              const rCurr = (r.curriculumName || "").trim();
              const rFull = (this.getFullGuruhNomi(r) || "").trim();

              const rClean = getClean(rGuruh);
              const rNorm = rGuruh.replace(/[^a-z0-9]/gi, '').toLowerCase();
              const rCurrNorm = rCurr.replace(/[^a-z0-9]/gi, '').toLowerCase();
              const rFullNorm = rFull.replace(/[^a-z0-9]/gi, '').toLowerCase();

              const rCode = (((rGuruh + " " + rCurr).match(/([a-zA-Z]{2,6}\s*\d{1,4}[a-zA-Z]?|\d+-\d+)/) || [])[0] || "").replace(/\s+/g, '').toUpperCase();

              const nameMatch = 
                (rClean && rClean === sepClean) ||
                (rGuruh && (rGuruh === sepRaw || rNorm === sepNorm)) ||
                (rCurr && (rCurr === sepRaw || rCurrNorm === sepNorm || rCurrNorm.includes(sepNorm) || sepNorm.includes(rCurrNorm))) ||
                (rFull && (rFull === sepRaw || rFullNorm === sepNorm)) ||
                (rCode && sepCode && rCode === sepCode);

              if (!nameMatch) return false;

              return sameSubject(row, r);
            });

            if (existingRow) {
              const sepId = existingRow._id || existingRow.itemId || existingRow.id;
              let patchData = {};

              if (this.activeTrainingType === 'maruza') {
                patchData.maruza = 0;
                patchData.oraliq = 0;
                patchData.yakuniy = 0;
              } else if (this.activeTrainingType === 'seminar') {
                patchData.seminar = 0;
              } else if (this.activeTrainingType === 'amaliy') {
                patchData.amaliy = 0;
              } else if (this.activeTrainingType === 'laboratoriya') {
                patchData.laboratoriya = 0;
              }

              if (sepId) {
                try {
                  await axios.patch(`${API}/kafedra-yuklamalar/${sepId}`, patchData);
                } catch (err) {
                  console.warn("Error updating secondary group row:", err);
                }
              }
              
              if (patchData.maruza !== undefined) existingRow.maruza = patchData.maruza;
              if (patchData.seminar !== undefined) existingRow.seminar = patchData.seminar;
              if (patchData.oraliq !== undefined) existingRow.oraliq = patchData.oraliq;
              if (patchData.yakuniy !== undefined) existingRow.yakuniy = patchData.yakuniy;
              if (patchData.amaliy !== undefined) existingRow.amaliy = patchData.amaliy;
              if (patchData.laboratoriya !== undefined) existingRow.laboratoriya = patchData.laboratoriya;
              
              existingRow.isSyntheticGroup = true;
            } else {
              // Group doesn't exist in DB for this subject, create a synthetic row
              let createData = { ...row };
              delete createData._id;
              delete createData.id;
              delete createData.itemId;
              delete createData.createdAt;
              delete createData.updatedAt;
              delete createData.__v;
              
              createData.guruhNomi = sepGroup.rawName;
              createData.guruhSoni = 1;
              createData.talabalarSoni = this.getGroupTalabaCount(sepGroup.rawName) || 1;

              if (this.activeTrainingType === 'maruza') {
                createData.maruza = 0;
                createData.oraliq = 0;
                createData.yakuniy = 0;
              } else if (this.activeTrainingType === 'seminar') {
                createData.seminar = 0;
              } else if (this.activeTrainingType === 'amaliy') {
                createData.amaliy = 0;
              } else if (this.activeTrainingType === 'laboratoriya') {
                createData.laboratoriya = 0;
              }

              createData.isSyntheticGroup = true;

              try {
                const res = await axios.post(`${API}/kafedra-yuklamalar`, createData);
                if (res.data && res.data.data) {
                  this.rows.push(res.data.data);
                }
              } catch (err) {
                console.warn("Error creating missing secondary group:", err);
              }
            }
          }
          this.addedGroupsForMaruza0 = [];
          this.selectedGroupForSeparate = null;
        }

        if (this.removedGroupsFromSubject && this.removedGroupsFromSubject.length > 0) {
          for (const remGName of this.removedGroupsFromSubject) {
            const remRaw = (remGName || "").trim();
            const remClean = getClean(remRaw);
            const remNorm = remRaw.replace(/[^a-z0-9]/gi, '').toLowerCase();
            const remCode = ((remRaw.match(/([a-zA-Z]{2,6}\s*\d{1,4}[a-zA-Z]?|\d+-\d+)/) || [])[0] || "").replace(/\s+/g, '').toUpperCase();

            let existingRow = this.rows.find(r => {
              if (r === row || (rowId && (r._id === rowId || r.id === rowId))) return false;
              if (!r.guruhNomi && !r.curriculumName) return false;
              const gList = (r.guruhNomi || "").split(',').map(s => s.trim()).filter(Boolean);
              if (gList.length > 1) return false;

              const rGuruh = (r.guruhNomi || "").trim();
              const rCurr = (r.curriculumName || "").trim();
              const rFull = (this.getFullGuruhNomi(r) || "").trim();

              const rClean = getClean(rGuruh);
              const rNorm = rGuruh.replace(/[^a-z0-9]/gi, '').toLowerCase();
              const rCurrNorm = rCurr.replace(/[^a-z0-9]/gi, '').toLowerCase();
              const rFullNorm = rFull.replace(/[^a-z0-9]/gi, '').toLowerCase();

              const rCode = (((rGuruh + " " + rCurr).match(/([a-zA-Z]{2,6}\s*\d{1,4}[a-zA-Z]?|\d+-\d+)/) || [])[0] || "").replace(/\s+/g, '').toUpperCase();

              const nameMatch = 
                (rClean && rClean === remClean) ||
                (rGuruh && (rGuruh === remRaw || rNorm === remNorm)) ||
                (rCurr && (rCurr === remRaw || rCurrNorm === remNorm || rCurrNorm.includes(remNorm) || remNorm.includes(rCurrNorm))) ||
                (rFull && (rFull === remRaw || rFullNorm === remNorm)) ||
                (rCode && remCode && rCode === remCode);

              if (!nameMatch) return false;

              return sameSubject(row, r);
            });

            if (existingRow) {
              const sepId = existingRow._id || existingRow.itemId || existingRow.id;
              
              // Only reset hours that were zeroed out by synthetic grouping
              let patchData = {};
              if (existingRow.maruza === 0) patchData.maruza = null;
              if (existingRow.seminar === 0) patchData.seminar = null;
              if (existingRow.amaliy === 0) patchData.amaliy = null;
              if (existingRow.laboratoriya === 0) patchData.laboratoriya = null;
              if (existingRow.oraliq === 0) patchData.oraliq = null;
              if (existingRow.yakuniy === 0) patchData.yakuniy = null;

              if (sepId && Object.keys(patchData).length > 0) {
                try {
                  await axios.patch(`${API}/kafedra-yuklamalar/${sepId}`, patchData);
                } catch (err) {
                  console.warn("Error restoring secondary group row:", err);
                }
              }
              
              if (patchData.maruza === null) existingRow.maruza = null;
              if (patchData.seminar === null) existingRow.seminar = null;
              if (patchData.oraliq === null) existingRow.oraliq = null;
              if (patchData.yakuniy === null) existingRow.yakuniy = null;
              if (patchData.amaliy === null) existingRow.amaliy = null;
              if (patchData.laboratoriya === null) existingRow.laboratoriya = null;
              
              const hasZeroHour = existingRow.maruza === 0 || existingRow.seminar === 0 || existingRow.amaliy === 0 || existingRow.laboratoriya === 0;
              if (!hasZeroHour) {
                delete existingRow.isSyntheticGroup;
              }
            }
          }
          this.removedGroupsFromSubject = [];
        }

        this.rows = [...this.rows];
        this.closeSubjectModal();
      } catch (err) {
        console.error("Saqlashda xatolik:", err);
        const errorMsg = err.response?.data?.error || err.response?.data?.message || err.message || "Noma'lum xatolik";
        alert("Saqlashda xatolik yuz berdi: " + errorMsg);
      } finally {
        this.isSavingModal = false;
      }
    },

    exportToExcel() {
      const table = this.$refs.yuklamaTable;
      if (!table) return;

      const fileName = (this.kafedraName || "Yuklama_jadval") 
        + (this.filters.oquvYili ? "_" + this.filters.oquvYili : "")
        + ".xlsx";

      try {
        const wb = XLSX.utils.table_to_book(table, { raw: true });
        XLSX.writeFile(wb, fileName.replace(/\s+/g, '_'));
      } catch (e) {
        console.error("XLSX export error, falling back:", e);
        const blob = new Blob([table.outerHTML], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName.replace(/\s+/g, '_');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    },
  },
};
</script>

<style scoped>
.yuklama-page {
  padding: 20px;
  font-family: "Segoe UI", sans-serif;
  background: #f5f6fa;
  min-height: 100vh;
}

.custom-workload-section {
  background: #fff;
  border-top: 1px solid #e5e7eb;
  padding: 20px 24px;
  border-radius: 0 0 8px 8px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.cw-title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #374151;
}

.cw-controls {
  display: flex;
  gap: 12px;
  align-items: center;
}

.cw-select {
  flex: 1;
  max-width: 400px;
}

.btn-add-custom {
  background-color: #8b5cf6;
}

.btn-add-custom:hover:not(:disabled) {
  background-color: #7c3aed;
}

.btn-add-custom:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.page-header {
  background: #f5f6fa;
  padding: 0 0 16px 0;
  margin-bottom: 0;
}

.breadcrumb {
  font-size: 14px;
  font-weight: 600;
  color: #6c757d;
  display: flex;
  align-items: center;
  gap: 8px;
  letter-spacing: 0.3px;
}

.crumb-link {
  color: #88929c;
  cursor: pointer;
  transition: color 0.2s;
}

.crumb-link:hover {
  color: #333;
}

.separator {
  color: #d1d5db;
  font-weight: 400;
  margin: 0 2px;
}

.crumb-current {
  color: #4b5563;
}

.actions-row {
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.filters-group {
  display: flex;
  gap: 12px;
}

  .filter-btn {
    padding: 6px 12px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    background-color: #fff;
    color: #374151;
    font-size: 13px;
    cursor: pointer;
    transition: background 0.2s, color 0.2s, border-color 0.2s;
  }

  .filter-btn.active {
    background-color: #4361ee;
    color: #fff;
    border-color: #4361ee;
  }

.filter-select {
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background-color: #fff;
  font-size: 13px;
  color: #374151;
  outline: none;
  cursor: pointer;
  min-width: 160px;
}
.filter-select:focus {
  border-color: #4361ee;
  box-shadow: 0 0 0 2px rgba(67, 97, 238, 0.1);
}

.action-buttons-right {
  display: flex;
  gap: 12px;
  align-items: center;
}

.btn-sync {
  background: #3b82f6; /* Slate/blue premium color */
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: background 0.2s, transform 0.1s;
}

.btn-sync:hover:not(:disabled) {
  background: #2563eb;
}

.btn-sync:disabled {
  background: #93c5fd;
  cursor: not-allowed;
}

.spinning {
  animation: spin-sync 1s linear infinite;
}

@keyframes spin-sync {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.btn-export {
  background: #28a745;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: background 0.2s;
}
.btn-export:hover {
  background: #218838;
}

/* Backup Button Group */
.backup-action-container {
  display: flex;
  align-items: center;
  position: relative;
}

.btn-backup-quick {
  background: #4f46e5;
  color: #fff;
  border: none;
  border-radius: 4px 0 0 4px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: background 0.2s;
}

.btn-backup-quick:hover:not(:disabled) {
  background: #4338ca;
}

.btn-backup-quick:disabled {
  background: #a5b4fc;
  cursor: not-allowed;
}

.btn-backup-caret {
  background: #4338ca;
  color: #fff;
  border: none;
  border-left: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 0 4px 4px 0;
  padding: 8px 10px;
  font-size: 11px;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-backup-caret:hover {
  background: #3730a3;
}

.backup-menu-dropdown {
  position: absolute;
  right: 0;
  top: calc(100% + 6px);
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 8px 10px -6px rgba(0, 0, 0, 0.08);
  min-width: 270px;
  z-index: 1000;
  overflow: hidden;
  padding: 6px 0;
}

.backup-menu-dropdown .menu-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 16px;
  cursor: pointer;
  transition: background 0.15s;
}

.backup-menu-dropdown .menu-item:hover {
  background: #f8fafc;
}

.menu-icon {
  font-size: 18px;
  line-height: 1;
}

.menu-content .menu-title {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
}

.menu-content .menu-desc {
  font-size: 11px;
  color: #64748b;
  margin-top: 2px;
}

.menu-divider {
  height: 1px;
  background: #e2e8f0;
  margin: 4px 0;
}

/* Settings Toggle */
.settings-action-container {
  position: relative;
}

.btn-settings-toggle {
  background: #ffffff;
  color: #475569;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 7px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.15s;
}

.btn-settings-toggle:hover {
  background: #f1f5f9;
  color: #0f172a;
  border-color: #94a3b8;
}

.settings-popover {
  position: absolute;
  right: 0;
  top: calc(100% + 6px);
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 14px 28px rgba(0, 0, 0, 0.12), 0 10px 10px rgba(0, 0, 0, 0.04);
  width: 280px;
  z-index: 1000;
  padding: 16px;
}

.popover-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.popover-title {
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
}

.btn-close-pop {
  background: none;
  border: none;
  font-size: 14px;
  color: #94a3b8;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
}

.btn-close-pop:hover {
  color: #334155;
  background: #f1f5f9;
}

.popover-subtitle {
  font-size: 12px;
  color: #64748b;
  margin: 6px 0 12px 0;
  line-height: 1.4;
}

.popover-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pop-check-label {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 6px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: #334155;
  transition: all 0.15s;
}

.pop-check-label:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.pop-check-label input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: #3b82f6;
  cursor: pointer;
}

.popover-footer {
  margin-top: 12px;
  padding-top: 8px;
  border-top: 1px solid #f1f5f9;
  text-align: right;
}


.table-wrapper {
  background: #fff;
  border-radius: 4px;
  border: 1px solid #e8e8e8;
  overflow: hidden;
  position: relative;
}

.loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.88);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  z-index: 10;
  font-size: 14px;
  color: #666;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e0e0e0;
  border-top-color: #4361ee;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}

.table-scroll {
  overflow-x: auto;
}

.yuklama-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
  min-width: 1800px;
}

.th-base {
  background: #fff;
  color: #555;
  font-weight: 600;
  text-align: center;
  vertical-align: middle;
  padding: 8px 4px;
  border: 1px solid #e8e8e8;
  font-size: 11px;
  line-height: 1.3;
}

.vertical-text {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  white-space: nowrap;
  margin: 0 auto;
  padding: 10px 0;
  min-height: 120px;
  display: flex;
  align-items: center;
}

.th-group {
  text-align: center;
  font-weight: 600;
  font-size: 11px;
  padding: 8px 4px;
  border: 1px solid #e8e8e8;
}

.th-audit {
  background: #fffdf0;
  color: #333;
}
.th-tashqari {
  background: #f0f8ff;
  color: #333;
}
.th-tashqari2 {
  background: #e6f7ff;
  color: #333;
}
.th-audit-cell {
  background: #fffdf0;
  color: #333;
}
.th-jami {
  background: #fffdf0;
  font-weight: 700;
}
.th-tashqari-cell {
  background: #f0f8ff;
  color: #333;
}
.th-tashqari2-cell {
  background: #e6f7ff;
  color: #333;
}

.th-num { width: 36px; }
.th-fan { width: 240px; min-width: 180px; }
.th-fak { width: 150px; min-width: 120px; }
.th-yon { width: 280px; min-width: 220px; }
.th-guruh-nomi { width: 250px; min-width: 200px; }

.data-row:hover { background: #fafafa; }

td {
  padding: 8px 6px;
  border: 1px solid #e8e8e8;
  vertical-align: middle;
  color: #333;
  font-size: 11px;
}

.totals-row td {
  background: #1e293b !important;
  color: #f8fafc !important;
  font-weight: 700;
  font-size: 11px;
  border-color: #334155;
  position: sticky;
  bottom: 0;
  z-index: 2;
}
.totals-row .td-audit {
  background: #1e3a5f !important;
  color: #bfdbfe !important;
}
.totals-row .td-tashqari {
  background: #1e3a2f !important;
  color: #bbf7d0 !important;
}
.totals-row .td-jami-val {
  background: #4c1d95 !important;
  color: #e9d5ff !important;
}
.td-total-label {
  text-align: center;
  letter-spacing: 1px;
  font-size: 12px;
}

.td-center { text-align: center; }
.td-left { text-align: left; }
.td-num { color: #333; }
.td-fan { font-weight: 500; color: #333; white-space: normal; word-break: break-word; }
.td-yon { min-width: 220px; max-width: 360px; width: 280px; color: #555; white-space: normal; word-break: break-word; line-height: 1.4; }
.td-guruh-nomi { min-width: 200px; max-width: 320px; width: 250px; white-space: normal; word-break: break-word; text-align: center; line-height: 1.4; font-weight: 500; }
.fan-code { color: #888; font-size: 10px; font-weight: 400; }

.td-audit { background: #fffdf0; }
.td-maruza { color: #8b008b; }
.td-amaliy { color: #2e8b57; }
.td-amaliy-multiplied {
  background: #fff3cd !important;
  color: #b45309;
  font-weight: 700;
}
.td-lab-multiplied {
  background: #e8f4fd !important;
  color: #1a6da8;
  font-weight: 700;
}

.td-jami-val {
  background: #fffdf0;
  font-weight: 700;
  color: #333;
}
.td-tashqari { background: #f0f8ff; }
.td-tashqari2 { background: #e6f7ff; }

.td-empty {
  text-align: center;
  color: #aaa;
  padding: 60px;
  font-size: 14px;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-top: 1px solid #f0f0f0;
}
.total-info { font-size: 13px; color: #888; }
.page-btns { display: flex; align-items: center; gap: 10px; }

.page-btn {
  background: #f0f0f0;
  border: none;
  border-radius: 4px;
  width: 30px;
  height: 30px;
  cursor: pointer;
  font-size: 16px;
  transition: background 0.2s;
}
.page-btn:hover:not(:disabled) {
  background: #28a745;
  color: #fff;
}
.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.page-info { font-size: 13px; color: #555; }

.editable-cell {
  cursor: pointer;
  transition: background 0.2s;
}
.editable-cell:hover {
  background: #e6f7ff;
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: #fff;
  border-radius: 6px;
  width: 400px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.subject-modal-content {
  width: 500px !important;
  max-width: 95vw;
  border-radius: 8px;
  overflow: hidden;
}

.tt-tab-row {
  display: flex;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  padding: 8px 12px;
  gap: 6px;
}

.tt-tab {
  flex: 1;
  padding: 6px 10px;
  font-size: 11px;
  font-weight: 600;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #ffffff;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.tt-tab:hover {
  background: #f1f5f9;
}

.tt-tab.active {
  background: #4361ee;
  color: #ffffff;
  border-color: #4361ee;
  box-shadow: 0 2px 4px rgba(67, 97, 238, 0.25);
}

.group-mode-box {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 6px;
  padding: 10px 12px;
  margin-top: 4px;
}

.group-mode-title {
  font-size: 12px;
  font-weight: 600;
  color: #166534;
  margin-bottom: 6px;
}

.mode-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #15803d;
  cursor: pointer;
  margin-bottom: 4px;
}

.mode-label:last-child {
  margin-bottom: 0;
}

.hours-section {
  background: #fafafa;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 10px 12px;
}

.hours-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-top: 8px;
}

.hour-field {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  padding: 4px 6px;
  border-radius: 4px;
  background: #fff;
  border: 1px solid #cbd5e1;
}

.hour-field.highlighted {
  border-color: #4361ee;
  background: #eff6ff;
}

.hour-inp {
  width: 60px !important;
  padding: 4px 6px !important;
  font-size: 12px !important;
}

.btn-zero {
  background: #ef4444;
  color: #fff;
  border: none;
  border-radius: 3px;
  width: 22px;
  height: 22px;
  font-size: 11px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.btn-zero:hover {
  background: #dc2626;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
}

.modal-header h3 {
  margin: 0;
  font-size: 16px;
  color: #3b4559;
}

.btn-close {
  background: none;
  border: none;
  font-size: 20px;
  color: #888;
  cursor: pointer;
}

.modal-body {
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.subject-modal-body {
  flex-direction: column;
  align-items: stretch;
  gap: 16px;
}

.subject-modal-body .modal-input {
  width: 100%;
  box-sizing: border-box;
}

.subject-modal-body .calc-preview {
  margin-top: 8px;
  font-size: 0.85em;
  color: #64748b;
  background: #f8fafc;
  padding: 10px;
  border-radius: 6px;
  border: 1px dashed #cbd5e1;
}

.modal-body label {
  font-size: 13px;
  color: #555;
  font-weight: 600;
}

.modal-input {
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 6px 12px;
  width: 100px;
  font-size: 14px;
  outline: none;
  transition: border 0.2s;
}
.modal-input:focus {
  border-color: #9b59b6;
  box-shadow: 0 0 0 2px rgba(155, 89, 182, 0.2);
}

.modal-footer {
  padding: 16px 20px;
  display: flex;
  gap: 12px;
}

.btn-clear {
  background: #e74c3c;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
}

.btn-confirm {
  background: #2ecc71;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
}

/* Custom Tashqari Formula Modal Styling */
.modal-content.modal-tashqari {
  width: 460px;
}

.tashqari-body {
  display: flex;
  flex-direction: column;
  align-items: stretch !important;
  gap: 16px;
  padding: 24px;
}

.formula-info {
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 6px;
  padding: 10px 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.formula-label {
  font-size: 12px;
  color: #64748b;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.formula-code {
  font-family: Consolas, Monaco, monospace;
  font-size: 13px;
  color: #4f46e5;
  background: #eeebff;
  padding: 3px 8px;
  border-radius: 4px;
  font-weight: bold;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group label {
  font-size: 13px;
  color: #475569;
  font-weight: 600;
}

.selection-section {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.selection-group {
  border-bottom: 1px solid #f1f5f9;
  padding-bottom: 12px;
}

.radio-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
}

.radio-label {
  font-size: 13px !important;
  color: #334155 !important;
  font-weight: normal !important;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.radio-label input {
  cursor: pointer;
  accent-color: #4f46e5;
}

.multi-inputs {
  display: flex;
  gap: 16px;
}

.multi-inputs .form-group {
  flex: 1;
}

.tashqari-body .modal-input {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s;
}

.tashqari-body .modal-input:focus {
  border-color: #4f46e5;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.15);
}

.calc-preview {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 6px;
  padding: 12px;
  margin-top: 4px;
}

.preview-title {
  display: block;
  font-size: 11px;
  color: #166534;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.preview-expr {
  font-size: 14px;
  color: #1e293b;
}

.preview-total-val {
  color: #15803d;
  font-size: 16px;
}

.btn-edit-row {
  background: #3b82f6;
  color: #ffffff;
  border: none;
  border-radius: 4px;
  width: 20px;
  height: 20px;
  line-height: 18px;
  text-align: center;
  font-size: 11px;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.btn-edit-row:hover {
  background: #2563eb;
}

.btn-delete-row {
  background: #ef4444;
  color: #ffffff;
  border: none;
  border-radius: 4px;
  width: 20px;
  height: 20px;
  line-height: 18px;
  text-align: center;
  font-size: 11px;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.btn-delete-row:hover {
  background: #dc2626;
}

.row-secondary {
  /* background-color: #fff9db !important; Soft yellow for secondary groups */
}

.row-primary {
  /* background-color: #f0fdf4 !important; Soft green for primary stream rows */
}

/* Kurs Filter Styles */
.filter-divider {
  width: 1px;
  height: 20px;
  background: #e2e8f0;
  margin: 0 6px;
}

.filter-label {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  margin-right: 4px;
}

.th-kurs-col {
  position: relative;
  vertical-align: top !important;
  padding: 6px 2px 2px !important;
}

.th-kurs-col.th-kurs-active {
  background-color: #eff6ff !important;
}

.th-kurs-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  position: relative;
}

.kurs-filter-btn-wrap {
  position: relative;
  display: inline-flex;
  justify-content: center;
  margin-bottom: 4px;
}

.th-kurs-filter-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  padding: 3px 5px;
  border-radius: 4px;
  border: 1px solid #cbd5e1;
  background: #f8fafc;
  color: #475569;
  cursor: pointer;
  transition: all 0.15s ease;
  font-size: 10px;
  font-weight: 700;
}

.th-kurs-filter-btn:hover {
  background: #e0e7ff;
  border-color: #818cf8;
  color: #3730a3;
}

.th-kurs-filter-btn.active {
  background: #2563eb;
  border-color: #1d4ed8;
  color: #ffffff;
  box-shadow: 0 1px 3px rgba(37, 99, 235, 0.3);
}

.th-kurs-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  color: #1d4ed8;
  border-radius: 50%;
  width: 13px;
  height: 13px;
  font-size: 9px;
  font-weight: 800;
}

.kurs-header-dropdown {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.18), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
  padding: 6px;
  min-width: 120px;
  z-index: 9999;
  text-align: left;
}

.kurs-dropdown-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 3px 6px 5px;
  font-size: 11px;
  font-weight: 700;
  color: #1e293b;
  border-bottom: 1px solid #f1f5f9;
  margin-bottom: 4px;
  white-space: nowrap;
}

.kurs-close-btn {
  background: transparent;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  padding: 0 2px;
}

.kurs-close-btn:hover {
  color: #ef4444;
}

.kurs-dropdown-option {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 8px;
  font-size: 11px;
  font-weight: 500;
  color: #334155;
  border-radius: 5px;
  cursor: pointer;
  transition: background 0.12s;
  white-space: nowrap;
}

.kurs-dropdown-option:hover {
  background: #f1f5f9;
  color: #0f172a;
}

.kurs-dropdown-option.selected {
  background: #eff6ff;
  color: #2563eb;
  font-weight: 700;
}

.opt-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #cbd5e1;
}

.kurs-dropdown-option.selected .opt-dot {
  background: #2563eb;
}

</style>
